<template>
    <div>
        <!-- Tabs for different sections -->
        <v-card elevation="0">
            <v-tabs v-model="activeTab" background-color="transparent" grow>
                <v-tab class="text-none">
                    <v-icon left small>mdi-file-document-multiple</v-icon>
                    {{ ptOnlyMode ? 'PT Submissions' : 'Submissions' }}
                    <v-badge v-if="ptOnlyMode ? ptPendingCount > 0 : pendingCount > 0" 
                        :content="ptOnlyMode ? ptPendingCount : pendingCount" 
                        :color="ptOnlyMode ? 'deep-purple' : 'warning'" inline
                        class="ml-2"></v-badge>
                </v-tab>
                <v-tab v-if="user && user.id === 122 && !ptOnlyMode" class="text-none">
                    <v-icon left small>mdi-account-cog</v-icon>
                    VA Profiles & Rates
                </v-tab>
                <v-tab v-if="!ptOnlyMode" class="text-none">
                    <v-icon left small>mdi-alert-circle</v-icon>
                    Exceptions
                    <v-badge v-if="unresolvedExceptionCount > 0" :content="unresolvedExceptionCount" color="error" inline
                        class="ml-2"></v-badge>
                </v-tab>
            </v-tabs>
            <v-divider></v-divider>

            <v-tabs-items v-model="activeTab">
                <!-- Submissions Tab -->
                <v-tab-item>
                    <!-- Inner tabs for Normal vs PT Submissions -->
                    <v-tabs v-model="submissionsViewTab" color="primary" class="mt-2">
                        <v-tab v-if="!ptOnlyMode" class="text-none">
                            <v-icon left small>mdi-file-document</v-icon>
                            Normal Submissions
                            <v-badge v-if="pendingCount > 0" :content="pendingCount" color="warning" inline class="ml-2"></v-badge>
                        </v-tab>
                        <v-tab class="text-none">
                            <v-icon left small>mdi-briefcase</v-icon>
                            PT Submissions
                            <v-badge v-if="ptPendingCount > 0" :content="ptPendingCount" color="deep-purple" inline class="ml-2"></v-badge>
                        </v-tab>
                    </v-tabs>
                    <v-divider></v-divider>

                    <v-tabs-items v-model="submissionsViewTab">
                        <!-- Normal Submissions Tab -->
                        <v-tab-item v-if="!ptOnlyMode">
                    <v-card-title class="pb-0">
                        <span style="font-size: 1.3rem; font-weight: 600">
                            Payroll Submissions
                        </span>
                    </v-card-title>

                    <!-- Filters Row -->
                    <v-card-text class="pb-0">
                        <!-- Row 1: Main Filters -->
                        <v-row dense align="center">
                            <!-- Date Range Filter -->
                            <v-col cols="12" sm="6" md="2">
                                <v-menu v-model="dateRangeMenu" :close-on-content-click="false"
                                    transition="scale-transition" offset-y min-width="auto">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="formattedDateRange" label="Submission Date Range" outlined dense
                                            readonly v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-range"
                                            clearable @click:clear="clearDateRange" hide-details></v-text-field>
                                    </template>
                                    <v-date-picker v-model="dateRange" range no-title scrollable
                                        @change="dateRangeMenu = false"></v-date-picker>
                                </v-menu>
                            </v-col>

                            <!-- Paid Date Range Filter -->
                            <v-col cols="12" sm="6" md="2">
                                <v-menu v-model="paidDateRangeMenu" :close-on-content-click="false"
                                    transition="scale-transition" offset-y min-width="auto">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="formattedPaidDateRange" label="Paid Date Range" outlined dense
                                            readonly v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-clock"
                                            clearable @click:clear="clearPaidDateRange" hide-details></v-text-field>
                                    </template>
                                    <v-date-picker v-model="paidDateRange" range no-title scrollable
                                        @change="paidDateRangeMenu = false"></v-date-picker>
                                </v-menu>
                            </v-col>

                            <!-- User Filter -->
                            <v-col cols="12" sm="6" md="2">
                                <v-autocomplete v-model="userFilter" :items="uniqueUsers" item-text="name"
                                    item-value="id" label="Employee" outlined dense clearable
                                    hide-details></v-autocomplete>
                            </v-col>

                            <!-- Company Filter -->
                            <v-col cols="12" sm="6" md="2">
                                <v-autocomplete v-model="companyFilter" :items="uniqueCompanies" item-text="name"
                                    item-value="id" label="Company" outlined dense clearable
                                    hide-details></v-autocomplete>
                            </v-col>

                            <!-- Currency Filter -->
                            <v-col cols="6" sm="4" md="1">
                                <v-select v-model="currencyFilter" :items="['All', 'USD', 'PHP', 'EUR', 'GBP']"
                                    label="Currency" outlined dense hide-details></v-select>
                            </v-col>

                            <!-- Quarter Filter -->
                            <v-col cols="6" sm="4" md="2">
                                <v-select v-model="quarterFilter" :items="quarterOptions" item-text="text"
                                    item-value="value" label="Quarter" outlined dense hide-details></v-select>
                            </v-col>
                        </v-row>

                        <!-- Row 2: Status, Sort, Rates, and Actions -->
                        <v-row dense align="center" class="mt-2">
                            <!-- Status Filter -->
                            <v-col cols="12" sm="6" md="3">
                                <v-btn-toggle v-model="statusFilter" dense mandatory class="d-flex">
                                    <v-btn value="all" class="text-none flex-grow-1" small>All</v-btn>
                                    <v-btn value="pending" class="text-none flex-grow-1" small>Pending</v-btn>
                                    <v-btn value="approved" class="text-none flex-grow-1" small>Approved</v-btn>
                                    <v-btn value="unpaid" class="text-none flex-grow-1" small>Unpaid</v-btn>
                                    <v-btn value="denied" class="text-none flex-grow-1" small>Denied</v-btn>
                                </v-btn-toggle>
                            </v-col>

                            <!-- Sort By -->
                            <v-col cols="8" sm="4" md="2">
                                <v-select
                                    v-model="submissionsSortBy"
                                    :items="submissionsSortOptions"
                                    item-text="text"
                                    item-value="value"
                                    label="Sort By"
                                    outlined dense hide-details
                                    prepend-inner-icon="mdi-sort"
                                ></v-select>
                            </v-col>
                            <v-col cols="4" sm="2" md="1">
                                <v-btn small outlined :color="submissionsSortDesc ? 'primary' : 'grey'" @click="submissionsSortDesc = !submissionsSortDesc" class="text-none" style="height: 40px; width: 100%;">
                                    <v-icon small>{{ submissionsSortDesc ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>
                                    {{ submissionsSortDesc ? 'Desc' : 'Asc' }}
                                </v-btn>
                            </v-col>

                            <!-- Rate Inputs -->
                            <v-col cols="6" sm="3" md="1">
                                <v-text-field v-model.number="dollarRate" label="Dollar Rate" outlined dense
                                    type="number" min="0" step="0.01" prefix="₱" :loading="isLoadingRate || isSavingRate"
                                    hide-details></v-text-field>
                            </v-col>
                            <v-col cols="6" sm="3" md="1">
                                <v-text-field v-model.number="transferFee" label="Transfer Fee" outlined dense
                                    type="number" min="0" step="0.01" prefix="₱" :loading="isLoadingRate || isSavingRate"
                                    hide-details></v-text-field>
                            </v-col>

                            <!-- Action Buttons -->
                            <v-col cols="12" sm="12" md="auto" class="d-flex flex-wrap" style="gap: 8px;">
                                <v-btn small outlined color="primary" class="text-none" @click="exportSubmissionsCSV">
                                    <v-icon left small>mdi-file-excel</v-icon>
                                    Export CSV
                                </v-btn>
                                <v-btn small outlined color="primary" class="text-none" @click="openExportInvoicesDialog">
                                    <v-icon left small>mdi-download-multiple</v-icon>
                                    Export Invoices
                                </v-btn>
                                <v-btn small :outlined="!showDeleted" :color="showDeleted ? 'error' : 'grey'" class="text-none" @click="toggleShowDeleted" :loading="isLoadingPaginatedSubmissions" v-if="canDeleteSubmissions">
                                    <v-icon left small>mdi-delete-restore</v-icon>
                                    {{ showDeleted ? 'Hide Deleted' : 'Show Deleted' }}
                                </v-btn>
                                <v-btn small outlined color="primary" class="text-none" @click="$emit('refresh')">
                                    <v-icon left small>mdi-refresh</v-icon>
                                    Refresh
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-card-text>
                        <v-data-table
                            :headers="submissionsHeaders"
                            :items="paginatedSubmissionsWithExpense"
                            :server-items-length="submissionsTotal"
                            :options.sync="submissionsOptions"
                            :loading="isLoadingPaginatedSubmissions"
                            :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                            class="elevation-1 scrollable-table"
                        >
                            <template #[`item.actions`]="{ item }">
                                <div class="d-flex align-center">
                                    <v-tooltip v-if="item.status === 'pending' && !item.deleted_at" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon color="success" v-bind="attrs" v-on="on" @click="openApproveDialog(item)">
                                                <v-icon small>mdi-check</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Approve</span>
                                    </v-tooltip>
                                    <v-tooltip v-if="item.status === 'approved' && !item.deleted_at && canDeleteSubmissions" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon color="warning" v-bind="attrs" v-on="on" @click="confirmUnapprove(item)">
                                                <v-icon small>mdi-undo</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Unapprove</span>
                                    </v-tooltip>
                                    <v-tooltip v-if="item.exceptions && item.exceptions.length > 0" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon color="orange" v-bind="attrs" v-on="on" @click="openExceptionsDialog(item)">
                                                <v-badge :content="item.exceptions.length" color="red" overlap>
                                                    <v-icon small>mdi-alert</v-icon>
                                                </v-badge>
                                            </v-btn>
                                        </template>
                                        <span>{{ item.exceptions.length }} exception(s) flagged</span>
                                    </v-tooltip>
                                    <v-tooltip v-if="canDeleteSubmissions && !item.deleted_at" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon color="error" v-bind="attrs" v-on="on" @click="confirmDeleteSubmission(item)">
                                                <v-icon small>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Delete submission</span>
                                    </v-tooltip>
                                    <v-tooltip v-if="canDeleteSubmissions && item.deleted_at" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon color="success" v-bind="attrs" v-on="on" @click="confirmRestoreSubmission(item)">
                                                <v-icon small>mdi-restore</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Restore submission</span>
                                    </v-tooltip>
                                </div>
                            </template>
                            <template #[`item.submitted_at`]="{ item }">
                                {{ formatDateTime(item.submitted_at) }}
                            </template>
                            <template #[`item.user`]="{ item }">
                                <div class="d-flex align-center py-2">
                                    <div>
                                        <div style="font-weight: 500">{{ item.user?.first_name && item.user?.last_name ? item.user.first_name + ' ' + item.user.last_name : (item.user?.name || '-') }}</div>
                                        <div class="text-caption grey--text">{{ item.user?.email || '-' }}</div>
                                    </div>
                                </div>
                            </template>
                            <template #[`item.invoice_period`]="{ item }">
                                {{ formatDateRange(item.invoice_period_from, item.invoice_period_to) }}
                            </template>
                            <template #[`item.company`]="{ item }">
                                {{ item.company?.name || '-' }}
                            </template>
                            <template #[`item.invoice_number`]="{ item }">
                                <span style="font-weight: 500">{{ item.invoice_number }}</span>
                            </template>
                            <template #[`item.hours_worked`]="{ item }">
                                <div>{{ formatHours(item.hours_worked) }}</div>
                                <div v-if="item.time_doctor_hours" class="text-caption primary--text" style="cursor: pointer; text-decoration: underline dotted;" title="See where this time came from (Classic / TD2 / DGTasks)" @click.stop="openTimeBreakdown(item, 'normal')">TD: {{ formatHours(item.time_doctor_hours) }}</div>
                            </template>
                            <template #[`item.invoice_amount`]="{ item }">
                                <div class="d-flex align-center" style="cursor: pointer;" @click="openCurrencyDialog(item, 'normal')">
                                    <span>{{ item.currency || 'USD' }} {{ formatCurrency(item.invoice_amount) }}</span>
                                    <v-icon x-small class="ml-1" color="grey">mdi-pencil</v-icon>
                                </div>
                                <div v-if="item.user && item.user.hourly_rate" class="text-caption grey--text">Calc:<br>{{ item.user.payment_currency || 'USD' }} {{ formatCurrency(item.hours_worked * item.user.hourly_rate) }}</div>
                            </template>
                            <template #[`item.invoice_file_url`]="{ item }">
                                <div class="d-flex align-center">
                                    <v-btn v-if="item.invoice_file_path" x-small icon color="primary"
                                        :href="`https://api.dgventureslimited.com/storage/${item.invoice_file_path}`"
                                        target="_blank">
                                        <v-icon small>mdi-file-pdf-box</v-icon>
                                    </v-btn>
                                    <span v-else class="grey--text">-</span>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon :color="item.invoice_file_path ? 'grey' : 'primary'" v-bind="attrs" v-on="on" @click="openInvoiceUploadDialog(item, false)">
                                                <v-icon x-small>mdi-upload</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ item.invoice_file_path ? 'Replace' : 'Upload' }} invoice PDF</span>
                                    </v-tooltip>
                                </div>
                            </template>
                            <template #[`item.additional_document_url`]="{ item }">
                                <div class="d-flex align-center">
                                    <v-btn v-if="item.additional_document_path" x-small icon color="teal"
                                        :href="`https://api.dgventureslimited.com/storage/${item.additional_document_path}`"
                                        target="_blank">
                                        <v-icon small>mdi-file-document-outline</v-icon>
                                    </v-btn>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon :color="item.additional_document_path ? 'grey' : 'primary'" v-bind="attrs" v-on="on" @click="openAdditionalDocDialog(item)">
                                                <v-icon x-small>mdi-upload</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ item.additional_document_path ? 'Replace' : 'Upload' }} additional document</span>
                                    </v-tooltip>
                                    <v-tooltip v-if="item.additional_document_path" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon color="error" v-bind="attrs" v-on="on" @click="confirmDeleteAdditionalDoc(item)">
                                                <v-icon x-small>mdi-close</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Remove additional document</span>
                                    </v-tooltip>
                                </div>
                            </template>
                            <template #[`item.comments`]="{ item }">
                                <v-tooltip v-if="item.comments" bottom max-width="300">
                                    <template v-slot:activator="{ on, attrs }">
                                        <span class="text-truncate" style="max-width: 150px; display: block; cursor: pointer;" v-bind="attrs" v-on="on">{{ item.comments }}</span>
                                    </template>
                                    <span>{{ item.comments }}</span>
                                </v-tooltip>
                                <span v-else class="grey--text">-</span>
                            </template>
                            <template #[`item.admin_comments`]="{ item }">
                                <v-textarea
                                    :value="item.admin_comments || ''"
                                    @change="updateAdminComments(item, $event)"
                                    rows="2"
                                    auto-grow
                                    dense
                                    hide-details
                                    placeholder="Admin notes..."
                                    style="min-width: 180px; font-size: 12px;"
                                ></v-textarea>
                            </template>
                            <template #[`item.cor_file_url`]="{ item }">
                                <v-btn v-if="item.cor_file_path" x-small icon color="primary"
                                    :href="`https://api.dgventureslimited.com/storage/${item.cor_file_path}`"
                                    target="_blank">
                                    <v-icon small>mdi-file-document</v-icon>
                                </v-btn>
                                <v-tooltip v-else-if="userHasPriorCor(item.user?.id, item.created_at)" bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon small color="success" v-bind="attrs" v-on="on">mdi-check-circle</v-icon>
                                    </template>
                                    <span>On file from previous submission</span>
                                </v-tooltip>
                                <span v-else class="grey--text">-</span>
                            </template>
                            <template #[`item.acknowledgement_receipt_url`]="{ item }">
                                <v-btn v-if="item.acknowledgement_receipt_path" x-small icon color="primary"
                                    :href="`https://api.dgventureslimited.com/storage/${item.acknowledgement_receipt_path}`"
                                    target="_blank">
                                    <v-icon small>mdi-file-document</v-icon>
                                </v-btn>
                                <span v-else class="grey--text">-</span>
                            </template>
                            <template #[`item.tax_return_urls`]="{ item }">
                                <template v-if="item.tax_return_urls && item.tax_return_urls.length > 0">
                                    <v-btn v-for="(tax, idx) in item.tax_return_urls" :key="idx" x-small icon
                                        color="primary" :href="`https://api.dgventureslimited.com/storage/${tax.path}`"
                                        target="_blank" class="mr-1">
                                        <v-icon small>mdi-file-document</v-icon>
                                    </v-btn>
                                </template>
                                <template v-else>
                                    <v-tooltip v-if="userHasTaxReturnsForQuarter(item.user?.id, item.created_at).hasTaxReturns" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small color="success" v-bind="attrs" v-on="on">mdi-check-circle</v-icon>
                                        </template>
                                        <span>Tax returns on file for this quarter</span>
                                    </v-tooltip>
                                    <v-tooltip v-else-if="!userHasTaxReturnsForQuarter(item.user?.id, item.created_at).isPastDue" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small color="warning" v-bind="attrs" v-on="on">mdi-alert</v-icon>
                                        </template>
                                        <span>Tax returns not yet uploaded for this quarter</span>
                                    </v-tooltip>
                                    <v-tooltip v-else bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small color="error" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                                        </template>
                                        <span>Tax returns overdue for this quarter</span>
                                    </v-tooltip>
                                </template>
                            </template>
                            <template #[`item.payment_receipt_uploaded_at`]="{ item }">
                                <div v-if="item.status === 'approved'" class="d-flex align-center justify-center">
                                    <v-menu
                                        :ref="`datePaidMenu_${item.id}`"
                                        v-model="datePaidMenus[item.id]"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="auto"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small text v-bind="attrs" v-on="on" class="text-none px-1">
                                                <span v-if="item.payment_receipt_uploaded_at">{{ $date(item.payment_receipt_uploaded_at).format('MMM D, YYYY') }}</span>
                                                <span v-else class="grey--text">-</span>
                                            </v-btn>
                                        </template>
                                        <v-date-picker
                                            :value="item.payment_receipt_uploaded_at ? $date(item.payment_receipt_uploaded_at).format('YYYY-MM-DD') : null"
                                            @input="updatePaymentDate(item, $event)"
                                            no-title
                                            scrollable
                                        ></v-date-picker>
                                    </v-menu>
                                    <v-tooltip v-if="!item.payment_receipt_uploaded_at" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon color="success" @click="setPaymentDateToday(item)" v-bind="attrs" v-on="on">
                                                <v-icon x-small>mdi-check</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Mark as paid today</span>
                                    </v-tooltip>
                                </div>
                                <div v-else style="text-align:center;">
                                    <span class="grey--text">-</span>
                                </div>
                            </template>
                            <template #[`item.status`]="{ item }">
                                <div class="d-flex align-center">
                                    <v-chip small :color="item.deleted_at ? 'grey' : getStatusColor(item.status)" text-color="white" label>
                                        {{ item.deleted_at ? 'deleted' : item.status }}
                                    </v-chip>
                                    <v-tooltip v-if="item.status === 'pending' && !item.deleted_at" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon color="success" class="ml-1" @click="openApproveDialog(item)" v-bind="attrs" v-on="on">
                                                <v-icon x-small>mdi-check</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Approve</span>
                                    </v-tooltip>
                                    <v-tooltip v-if="item.status === 'approved' && !item.deleted_at && canDeleteSubmissions" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon color="warning" class="ml-1" @click="confirmUnapprove(item)" v-bind="attrs" v-on="on">
                                                <v-icon x-small>mdi-undo</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Unapprove</span>
                                    </v-tooltip>
                                </div>
                            </template>
                            <template #[`item.approved_amount`]="{ item }">
                                <span v-if="item.approved_amount" style="color: #4caf50; font-weight: 600;">
                                    {{ item.currency || 'USD' }} {{ formatCurrency(item.approved_amount) }}
                                </span>
                                <span v-else class="grey--text">-</span>
                            </template>
                            <template #[`item.dollar_rate_display`]="{ item }">
                                <div v-if="item.currency === 'USD' && item.status !== 'denied' && !item.deleted_at" class="d-flex align-center">
                                    <v-edit-dialog
                                        :return-value.sync="item.dollar_rate"
                                        @save="saveDollarRate(item)"
                                        large
                                        persistent
                                    >
                                        <span class="editable-field">₱{{ formatCurrency(item.submission_dollar_rate) }}</span>
                                        <template v-slot:input>
                                            <v-text-field
                                                v-model.number="editingDollarRate"
                                                label="Dollar Rate"
                                                type="number"
                                                prefix="₱"
                                                single-line
                                                dense
                                                @focus="editingDollarRate = item.submission_dollar_rate"
                                            ></v-text-field>
                                        </template>
                                    </v-edit-dialog>
                                </div>
                                <span v-else class="grey--text">-</span>
                            </template>
                            <template #[`item.php_rate`]="{ item }">
                                <span v-if="(item.currency === 'USD' || item.currency === 'PHP') && item.status !== 'denied' && !item.deleted_at"
                                    style="font-weight: 600; color: #1976d2">₱{{ formatCurrency(item.php_rate) }}</span>
                                <span v-else class="grey--text">-</span>
                            </template>
                            <template #[`item.transfer_fee_display`]="{ item }">
                                <div v-if="(item.currency === 'USD' || item.currency === 'PHP') && item.status !== 'denied' && !item.deleted_at" 
                                    class="d-flex align-center">
                                    <v-edit-dialog
                                        :return-value.sync="item.transfer_fee"
                                        @save="saveTransferFee(item)"
                                        large
                                        persistent
                                    >
                                        <span class="editable-field">₱{{ formatCurrency(item.submission_transfer_fee) }}</span>
                                        <template v-slot:input>
                                            <v-text-field
                                                v-model.number="editingTransferFee"
                                                label="Transfer Fee"
                                                type="number"
                                                prefix="₱"
                                                single-line
                                                dense
                                                @focus="editingTransferFee = item.submission_transfer_fee"
                                            ></v-text-field>
                                        </template>
                                    </v-edit-dialog>
                                </div>
                                <span v-else class="grey--text">-</span>
                            </template>
                            <template #[`item.total_salary_expense`]="{ item }">
                                <span v-if="(item.currency === 'USD' || item.currency === 'PHP') && item.status !== 'denied' && !item.deleted_at"
                                    style="font-weight: 700; color: #388e3c">₱{{
                                        formatCurrency(item.total_salary_expense) }}</span>
                                <span v-else class="grey--text">-</span>
                            </template>
                        </v-data-table>

                        <!-- Summary (all filtered data — server-side totals) -->
                        <v-row class="mt-4" v-if="serverTotals.total_salary_expense > 0 || serverTotals.total_usd_approved > 0">
                            <v-col cols="12">
                                <v-alert type="info" outlined dense>
                                    <v-row>
                                        <v-col cols="12" md="3">
                                            <strong>Total USD Approved:</strong>
                                            ${{ formatCurrency(totalUsdApproved) }}
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <strong>Total PHP Rate:</strong>
                                            ₱{{ formatCurrency(totalPhpRate) }}
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <strong>Total Transfer Fees:</strong>
                                            ₱{{ formatCurrency(totalTransferFees) }}
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <strong>Total Salary Expense:</strong>
                                            <span style="font-weight: 700; color: #388e3c">₱{{
                                                formatCurrency(totalSalaryExpense) }}</span>
                                        </v-col>
                                    </v-row>
                                    <div class="text-caption grey--text mt-1">(all filtered results)</div>
                                </v-alert>
                            </v-col>
                        </v-row>

                        <div v-if="paginatedSubmissionsWithExpense.length === 0 && !isLoadingPaginatedSubmissions"
                            class="text-center py-8">
                            <v-icon size="64" color="grey lighten-1">mdi-file-document-outline</v-icon>
                            <div class="mt-2 grey--text">No submissions found</div>
                        </div>
                    </v-card-text>
                        </v-tab-item>

                        <!-- PT Submissions Tab -->
                        <v-tab-item>
                            <v-card-title class="pb-0">
                                <span style="font-size: 1.3rem; font-weight: 600">
                                    PT Payroll Submissions
                                </span>
                            </v-card-title>

                            <!-- PT Filters Row -->
                            <v-card-text class="pb-0">
                                <!-- Row 1: Main Filters -->
                                <v-row dense align="center">
                                    <!-- Date Range Filter -->
                                    <v-col cols="12" sm="6" md="2">
                                        <v-menu v-model="ptDateRangeMenu" :close-on-content-click="false"
                                            transition="scale-transition" offset-y min-width="auto">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field v-model="formattedPTDateRange" label="Submission Date Range" outlined dense
                                                    readonly v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-range"
                                                    clearable @click:clear="clearPTDateRange" hide-details></v-text-field>
                                            </template>
                                            <v-date-picker v-model="ptDateRange" range no-title scrollable
                                                @change="ptDateRangeMenu = false"></v-date-picker>
                                        </v-menu>
                                    </v-col>

                                    <!-- Paid Date Range Filter -->
                                    <v-col cols="12" sm="6" md="2">
                                        <v-menu v-model="ptPaidDateRangeMenu" :close-on-content-click="false"
                                            transition="scale-transition" offset-y min-width="auto">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field v-model="formattedPTPaidDateRange" label="Paid Date Range" outlined dense
                                                    readonly v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-clock"
                                                    clearable @click:clear="clearPTPaidDateRange" hide-details></v-text-field>
                                            </template>
                                            <v-date-picker v-model="ptPaidDateRange" range no-title scrollable
                                                @change="ptPaidDateRangeMenu = false"></v-date-picker>
                                        </v-menu>
                                    </v-col>

                                    <!-- User Filter -->
                                    <v-col cols="12" sm="6" md="2">
                                        <v-autocomplete v-model="ptUserFilter" :items="uniquePTUsers" item-text="name"
                                            item-value="id" label="Employee" outlined dense clearable
                                            hide-details></v-autocomplete>
                                    </v-col>

                                    <!-- Company Filter -->
                                    <v-col cols="12" sm="6" md="2">
                                        <v-autocomplete v-model="ptCompanyFilter" :items="uniquePTCompanies" item-text="name"
                                            item-value="id" label="Company" outlined dense clearable
                                            hide-details></v-autocomplete>
                                    </v-col>

                                    <!-- Currency Filter -->
                                    <v-col cols="6" sm="4" md="1">
                                        <v-select v-model="ptCurrencyFilter" :items="['All', 'USD', 'PHP']"
                                            label="Currency" outlined dense hide-details></v-select>
                                    </v-col>

                                    <!-- Quarter Filter -->
                                    <v-col cols="6" sm="4" md="2">
                                        <v-select v-model="ptQuarterFilter" :items="ptQuarterOptions" item-text="text"
                                            item-value="value" label="Quarter" outlined dense hide-details></v-select>
                                    </v-col>
                                </v-row>

                                <!-- Row 2: PT Status Field Filters -->
                                <v-row dense align="center" class="mt-2">
                                    <v-col cols="12" sm="6" md="2">
                                        <v-autocomplete
                                            v-model="ptClientNameFilter"
                                            :items="ptClientNameOptions"
                                            label="Client Name"
                                            outlined dense clearable hide-details
                                            multiple small-chips deletable-chips
                                        >
                                            <template v-slot:selection="{ item, index }">
                                                <v-chip v-if="index < 3" small close @click:close="ptClientNameFilter.splice(ptClientNameFilter.indexOf(item), 1)">
                                                    <span>{{ item }}</span>
                                                </v-chip>
                                                <span v-if="index === 3" class="grey--text text-caption">
                                                    (+{{ ptClientNameFilter.length - 3 }} more)
                                                </span>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="2">
                                        <v-select
                                            v-model="ptInvoiceStatusFilter"
                                            :items="[{ text: 'All Invoice Statuses', value: null }, ...invoiceStatusOptions.map(o => ({ text: o, value: o }))]"
                                            item-text="text"
                                            item-value="value"
                                            label="Invoice Status"
                                            outlined dense clearable hide-details
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="2">
                                        <v-select
                                            v-model="ptArStatusFilter"
                                            :items="[{ text: 'All A/R Statuses', value: null }, ...arStatusOptions.map(o => ({ text: o, value: o }))]"
                                            item-text="text"
                                            item-value="value"
                                            label="A/R Status"
                                            outlined dense clearable hide-details
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="2">
                                        <v-select
                                            v-model="ptBankDetailsFilter"
                                            :items="[{ text: 'All Bank Details', value: null }, ...bankDetailsOptions.map(o => ({ text: o, value: o }))]"
                                            item-text="text"
                                            item-value="value"
                                            label="Bank Details"
                                            outlined dense clearable hide-details
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="3">
                                        <v-select
                                            v-model="ptClientPaymentStatusFilter"
                                            :items="[{ text: 'All Client Payment Statuses', value: null }, ...clientPaymentStatusOptions.map(o => ({ text: o, value: o }))]"
                                            item-text="text"
                                            item-value="value"
                                            label="Client Payment Status"
                                            outlined dense clearable hide-details
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="3">
                                        <v-select
                                            v-model="ptTimeDoctorStatusFilter"
                                            :items="[{ text: 'All Time Doctor Statuses', value: null }, ...timeDoctorStatusOptions.map(o => ({ text: o, value: o }))]"
                                            item-text="text"
                                            item-value="value"
                                            label="Time Doctor Status"
                                            outlined dense clearable hide-details
                                        ></v-select>
                                    </v-col>
                                </v-row>

                                <!-- Row 3: Status, Sort, Rates, and Actions -->
                                <v-row dense align="center" class="mt-2">
                                    <!-- Status Filter -->
                                    <v-col cols="12" sm="6" md="3">
                                        <v-btn-toggle v-model="ptStatusFilter" dense mandatory class="d-flex">
                                            <v-btn value="all" class="text-none flex-grow-1" small>All</v-btn>
                                            <v-btn value="pending" class="text-none flex-grow-1" small>Pending</v-btn>
                                            <v-btn value="approved" class="text-none flex-grow-1" small>Approved</v-btn>
                                            <v-btn value="unpaid" class="text-none flex-grow-1" small>Unpaid</v-btn>
                                            <v-btn value="denied" class="text-none flex-grow-1" small>Denied</v-btn>
                                        </v-btn-toggle>
                                    </v-col>

                                    <!-- Sort By -->
                                    <v-col cols="8" sm="4" md="2">
                                        <v-select
                                            v-model="ptSubmissionsSortBy"
                                            :items="submissionsSortOptions"
                                            item-text="text"
                                            item-value="value"
                                            label="Sort By"
                                            outlined dense hide-details
                                            prepend-inner-icon="mdi-sort"
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="4" sm="2" md="1">
                                        <v-btn small outlined :color="ptSubmissionsSortDesc ? 'primary' : 'grey'" @click="ptSubmissionsSortDesc = !ptSubmissionsSortDesc" class="text-none" style="height: 40px; width: 100%;">
                                            <v-icon small>{{ ptSubmissionsSortDesc ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>
                                            {{ ptSubmissionsSortDesc ? 'Desc' : 'Asc' }}
                                        </v-btn>
                                    </v-col>

                                    <!-- Rate Inputs -->
                                    <v-col cols="6" sm="3" md="1">
                                        <v-text-field v-model.number="dollarRate" label="Dollar Rate" outlined dense
                                            type="number" min="0" step="0.01" prefix="₱" :loading="isLoadingRate || isSavingRate"
                                            hide-details></v-text-field>
                                    </v-col>
                                    <v-col cols="6" sm="3" md="1">
                                        <v-text-field v-model.number="transferFee" label="Transfer Fee" outlined dense
                                            type="number" min="0" step="0.01" prefix="₱" :loading="isLoadingRate || isSavingRate"
                                            hide-details></v-text-field>
                                    </v-col>

                                    <!-- Action Buttons -->
                                    <v-col cols="12" sm="12" md="auto" class="d-flex flex-wrap" style="gap: 8px;">
                                        <v-btn small outlined color="primary" class="text-none" @click="exportPTSubmissionsCSV">
                                            <v-icon left small>mdi-file-excel</v-icon>
                                            Export CSV
                                        </v-btn>
                                        <v-btn small outlined color="primary" class="text-none" @click="openExportPTInvoicesDialog">
                                            <v-icon left small>mdi-download-multiple</v-icon>
                                            Export Invoices
                                        </v-btn>
                                        <v-btn v-if="canDeleteSubmissions" small :outlined="!ptShowDeleted" :color="ptShowDeleted ? 'error' : 'grey'" class="text-none" @click="togglePTShowDeleted" :loading="isLoadingPaginatedPTSubmissions">
                                            <v-icon left small>mdi-delete-restore</v-icon>
                                            {{ ptShowDeleted ? 'Hide Deleted' : 'Show Deleted' }}
                                        </v-btn>
                                        <v-btn small outlined color="primary" class="text-none" @click="$emit('refresh')">
                                            <v-icon left small>mdi-refresh</v-icon>
                                            Refresh
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>

                            <v-card-text>
                                <v-data-table
                                    :headers="ptSubmissionsHeaders"
                                    :items="paginatedPTSubmissionsWithExpense"
                                    :server-items-length="ptSubmissionsTotal"
                                    :options.sync="ptSubmissionsOptions"
                                    :loading="isLoadingPaginatedPTSubmissions"
                                    :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                                    class="elevation-1 scrollable-table"
                                >
                                    <template #[`item.actions`]="{ item }">
                                        <div class="d-flex align-center">
                                            <v-tooltip v-if="item.status === 'pending' && !item.deleted_at" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon color="success" v-bind="attrs" v-on="on" @click="openPTApproveDialog(item)">
                                                        <v-icon small>mdi-check</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Approve</span>
                                            </v-tooltip>
                                            <v-tooltip v-if="item.status === 'approved' && !item.deleted_at && canDeleteSubmissions" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon color="warning" v-bind="attrs" v-on="on" @click="confirmPTUnapprove(item)">
                                                        <v-icon small>mdi-undo</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Unapprove</span>
                                            </v-tooltip>
                                            <v-tooltip v-if="item.exceptions && item.exceptions.length > 0" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon color="orange" v-bind="attrs" v-on="on" @click="openExceptionsDialog(item)">
                                                        <v-badge :content="item.exceptions.length" color="red" overlap>
                                                            <v-icon small>mdi-alert</v-icon>
                                                        </v-badge>
                                                    </v-btn>
                                                </template>
                                                <span>{{ item.exceptions.length }} exception(s) flagged</span>
                                            </v-tooltip>
                                            <v-tooltip v-if="canDeleteSubmissions && !item.deleted_at" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon color="error" v-bind="attrs" v-on="on" @click="confirmPTDeleteSubmission(item)">
                                                        <v-icon small>mdi-delete</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Delete submission</span>
                                            </v-tooltip>
                                            <v-tooltip v-if="canDeleteSubmissions && item.deleted_at" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon color="success" v-bind="attrs" v-on="on" @click="confirmPTRestoreSubmission(item)">
                                                        <v-icon small>mdi-restore</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Restore submission</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                    <template #[`item.submitted_at`]="{ item }">
                                        {{ formatDateTime(item.submitted_at) }}
                                    </template>
                                    <template #[`item.user`]="{ item }">
                                        <div class="d-flex align-center py-2">
                                            <div>
                                                <div style="font-weight: 500">{{ item.user?.first_name && item.user?.last_name ? item.user.first_name + ' ' + item.user.last_name : (item.user?.name || '-') }}</div>
                                                <div class="text-caption grey--text">{{ item.user?.email || '-' }}</div>
                                            </div>
                                        </div>
                                    </template>
                                    <template #[`item.client_name`]="{ item }">
                                        <span style="font-weight: 500">{{ item.client_name || '-' }}</span>
                                    </template>
                                    <template #[`item.invoice_period`]="{ item }">
                                        {{ formatDateRange(item.invoice_period_from, item.invoice_period_to) }}
                                    </template>
                                    <template #[`item.company`]="{ item }">
                                        {{ item.company?.name || '-' }}
                                    </template>
                                    <template #[`item.invoice_number`]="{ item }">
                                        <span style="font-weight: 500">{{ item.invoice_number }}</span>
                                    </template>
                                    <template #[`item.hours_worked`]="{ item }">
                                        <div>{{ formatHours(item.hours_worked) }}</div>
                                        <div v-if="item.time_doctor_hours" class="text-caption primary--text" style="cursor: pointer; text-decoration: underline dotted;" title="See where this time came from (Classic / TD2 / DGTasks)" @click.stop="openTimeBreakdown(item, 'pt')">TD: {{ formatHours(item.time_doctor_hours) }}</div>
                                    </template>
                                    <template #[`item.invoice_amount`]="{ item }">
                                        <div class="d-flex align-center" style="cursor: pointer;" @click="openCurrencyDialog(item, 'pt')">
                                            <span>{{ item.currency || 'USD' }} {{ formatCurrency(item.invoice_amount) }}</span>
                                            <v-icon x-small class="ml-1" color="grey">mdi-pencil</v-icon>
                                        </div>
                                        <div v-if="item.user && item.user.hourly_rate" class="text-caption grey--text">Calc:<br>{{ item.currency || 'USD' }} {{ formatCurrency(item.hours_worked * item.user.hourly_rate) }}</div>
                                    </template>
                                    <template #[`item.invoice_file_url`]="{ item }">
                                        <div class="d-flex align-center">
                                            <v-btn v-if="item.invoice_file_path" x-small icon color="primary"
                                                :href="`https://api.dgventureslimited.com/storage/${item.invoice_file_path}`"
                                                target="_blank">
                                                <v-icon small>mdi-file-pdf-box</v-icon>
                                            </v-btn>
                                            <span v-else class="grey--text">-</span>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon :color="item.invoice_file_path ? 'grey' : 'primary'" v-bind="attrs" v-on="on" @click="openInvoiceUploadDialog(item, true)">
                                                        <v-icon x-small>mdi-upload</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>{{ item.invoice_file_path ? 'Replace' : 'Upload' }} invoice PDF</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                    <template #[`item.capacity_form_url`]="{ item }">
                                        <v-btn v-if="item.capacity_utilization_form_path" x-small icon color="deep-purple"
                                            :href="`https://api.dgventureslimited.com/storage/${item.capacity_utilization_form_path}`"
                                            target="_blank">
                                            <v-icon small>mdi-clipboard-check</v-icon>
                                        </v-btn>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.overtime_approval_url`]="{ item }">
                                        <v-btn v-if="item.overtime_approval_path" x-small icon color="orange"
                                            :href="`https://api.dgventureslimited.com/storage/${item.overtime_approval_path}`"
                                            target="_blank">
                                            <v-icon small>mdi-clock-check</v-icon>
                                        </v-btn>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.employee_db_form_url`]="{ item }">
                                        <v-btn v-if="item.employee_database_form_path" x-small icon color="teal"
                                            :href="`https://api.dgventureslimited.com/storage/${item.employee_database_form_path}`"
                                            target="_blank">
                                            <v-icon small>mdi-database-check</v-icon>
                                        </v-btn>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.signature_ack_url`]="{ item }">
                                        <v-btn v-if="item.signature_acknowledgement_path" x-small icon color="indigo"
                                            :href="`https://api.dgventureslimited.com/storage/${item.signature_acknowledgement_path}`"
                                            target="_blank">
                                            <v-icon small>mdi-signature-freehand</v-icon>
                                        </v-btn>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.acknowledgement_receipt_url`]="{ item }">
                                        <v-btn v-if="item.acknowledgement_receipt_path" x-small icon color="deep-purple"
                                            :href="`https://api.dgventureslimited.com/storage/${item.acknowledgement_receipt_path}`"
                                            target="_blank">
                                            <v-icon small>mdi-receipt</v-icon>
                                        </v-btn>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.cor_file_url`]="{ item }">
                                        <v-btn v-if="item.cor_file_path" x-small icon color="primary"
                                            :href="`https://api.dgventureslimited.com/storage/${item.cor_file_path}`"
                                            target="_blank">
                                            <v-icon small>mdi-file-document</v-icon>
                                        </v-btn>
                                        <v-tooltip v-else-if="ptUserHasPriorCor(item.user?.id, item.created_at)" bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon small color="success" v-bind="attrs" v-on="on">mdi-check-circle</v-icon>
                                            </template>
                                            <span>On file from previous submission</span>
                                        </v-tooltip>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.tax_return_urls`]="{ item }">
                                        <template v-if="item.tax_return_urls && item.tax_return_urls.length > 0">
                                            <v-btn v-for="(tax, idx) in item.tax_return_urls" :key="idx" x-small icon
                                                color="primary" :href="`https://api.dgventureslimited.com/storage/${tax.path}`"
                                                target="_blank" class="mr-1">
                                                <v-icon small>mdi-file-document</v-icon>
                                            </v-btn>
                                        </template>
                                        <template v-else>
                                            <v-tooltip v-if="ptUserHasTaxReturnsForQuarter(item.user?.id, item.created_at).hasTaxReturns" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon small color="success" v-bind="attrs" v-on="on">mdi-check-circle</v-icon>
                                                </template>
                                                <span>Tax returns on file for this quarter</span>
                                            </v-tooltip>
                                            <v-tooltip v-else-if="!ptUserHasTaxReturnsForQuarter(item.user?.id, item.created_at).isPastDue" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon small color="warning" v-bind="attrs" v-on="on">mdi-alert</v-icon>
                                                </template>
                                                <span>Tax returns not yet uploaded for this quarter</span>
                                            </v-tooltip>
                                            <v-tooltip v-else bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon small color="error" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                                                </template>
                                                <span>Tax returns overdue for this quarter</span>
                                            </v-tooltip>
                                        </template>
                                    </template>
                                    <template #[`item.additional_document_url`]="{ item }">
                                        <div class="d-flex align-center">
                                            <v-btn v-if="item.additional_document_path" x-small icon color="teal"
                                                :href="`https://api.dgventureslimited.com/storage/${item.additional_document_path}`"
                                                target="_blank">
                                                <v-icon small>mdi-file-document-outline</v-icon>
                                            </v-btn>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon :color="item.additional_document_path ? 'grey' : 'primary'" v-bind="attrs" v-on="on" @click="openPTAdditionalDocDialog(item)">
                                                        <v-icon x-small>mdi-upload</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>{{ item.additional_document_path ? 'Replace' : 'Upload' }} additional document</span>
                                            </v-tooltip>
                                            <v-tooltip v-if="item.additional_document_path" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon color="error" v-bind="attrs" v-on="on" @click="confirmDeletePTAdditionalDoc(item)">
                                                        <v-icon x-small>mdi-close</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Remove additional document</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                    <template #[`item.comments`]="{ item }">
                                        <v-tooltip v-if="item.comments" bottom max-width="300">
                                            <template v-slot:activator="{ on, attrs }">
                                                <span class="text-truncate" style="max-width: 150px; display: block; cursor: pointer;" v-bind="attrs" v-on="on">{{ item.comments }}</span>
                                            </template>
                                            <span>{{ item.comments }}</span>
                                        </v-tooltip>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.admin_comments`]="{ item }">
                                        <v-textarea
                                            :value="item.admin_comments || ''"
                                            @change="updatePTAdminComments(item, $event)"
                                            rows="2"
                                            auto-grow
                                            dense
                                            hide-details
                                            placeholder="Admin notes..."
                                            style="min-width: 180px; font-size: 12px;"
                                        ></v-textarea>
                                    </template>
                                    <template #[`item.invoice_status`]="{ item }">
                                        <v-select
                                            :value="item.invoice_status"
                                            @change="updatePTStatusField(item, 'invoice_status', $event)"
                                            :items="invoiceStatusOptions"
                                            dense
                                            hide-details
                                            placeholder="-"
                                            style="font-size: 16px;"
                                        >
                                            <template v-slot:selection="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('invoice_status', val)" :text-color="getStatusFieldTextColor('invoice_status', val)">{{ val }}</v-chip>
                                            </template>
                                            <template v-slot:item="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('invoice_status', val)" :text-color="getStatusFieldTextColor('invoice_status', val)">{{ val }}</v-chip>
                                            </template>
                                        </v-select>
                                    </template>
                                    <template #[`item.ar_status`]="{ item }">
                                        <v-select
                                            :value="item.ar_status"
                                            @change="updatePTStatusField(item, 'ar_status', $event)"
                                            :items="arStatusOptions"
                                            dense
                                            hide-details
                                            placeholder="-"
                                            style="font-size: 16px;"
                                        >
                                            <template v-slot:selection="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('ar_status', val)" :text-color="getStatusFieldTextColor('ar_status', val)">{{ val }}</v-chip>
                                            </template>
                                            <template v-slot:item="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('ar_status', val)" :text-color="getStatusFieldTextColor('ar_status', val)">{{ val }}</v-chip>
                                            </template>
                                        </v-select>
                                    </template>
                                    <template #[`item.bank_details`]="{ item }">
                                        <v-select
                                            :value="item.bank_details"
                                            @change="updatePTStatusField(item, 'bank_details', $event)"
                                            :items="bankDetailsOptions"
                                            dense
                                            hide-details
                                            placeholder="-"
                                            style="font-size: 16px;"
                                        >
                                            <template v-slot:selection="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('bank_details', val)" :text-color="getStatusFieldTextColor('bank_details', val)">{{ val }}</v-chip>
                                            </template>
                                            <template v-slot:item="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('bank_details', val)" :text-color="getStatusFieldTextColor('bank_details', val)">{{ val }}</v-chip>
                                            </template>
                                        </v-select>
                                    </template>
                                    <template #[`item.client_payment_status`]="{ item }">
                                        <v-select
                                            :value="item.client_payment_status"
                                            @change="updatePTStatusField(item, 'client_payment_status', $event)"
                                            :items="clientPaymentStatusOptions"
                                            dense
                                            hide-details
                                            placeholder="-"
                                            style="font-size: 16px;"
                                        >
                                            <template v-slot:selection="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('client_payment_status', val)" :text-color="getStatusFieldTextColor('client_payment_status', val)">{{ val }}</v-chip>
                                            </template>
                                            <template v-slot:item="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('client_payment_status', val)" :text-color="getStatusFieldTextColor('client_payment_status', val)">{{ val }}</v-chip>
                                            </template>
                                        </v-select>
                                    </template>
                                    <template #[`item.time_doctor_status`]="{ item }">
                                        <v-select
                                            :value="item.time_doctor_status"
                                            @change="updatePTStatusField(item, 'time_doctor_status', $event)"
                                            :items="timeDoctorStatusOptions"
                                            dense
                                            hide-details
                                            placeholder="-"
                                            style="font-size: 16px;"
                                        >
                                            <template v-slot:selection="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('time_doctor_status', val)" :text-color="getStatusFieldTextColor('time_doctor_status', val)">{{ val }}</v-chip>
                                            </template>
                                            <template v-slot:item="{ item: val }">
                                                <v-chip small :color="getStatusFieldColor('time_doctor_status', val)" :text-color="getStatusFieldTextColor('time_doctor_status', val)">{{ val }}</v-chip>
                                            </template>
                                        </v-select>
                                    </template>
                                    <template #[`item.payment_receipt_uploaded_at`]="{ item }">
                                        <div v-if="item.status === 'approved'" class="d-flex align-center justify-center">
                                            <v-menu
                                                :ref="`ptDatePaidMenu_${item.id}`"
                                                v-model="ptDatePaidMenus[item.id]"
                                                :close-on-content-click="false"
                                                transition="scale-transition"
                                                offset-y
                                                min-width="auto"
                                            >
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small text v-bind="attrs" v-on="on" class="text-none px-1">
                                                        <span v-if="item.payment_receipt_uploaded_at">{{ $date(item.payment_receipt_uploaded_at).format('MMM D, YYYY') }}</span>
                                                        <span v-else class="grey--text">-</span>
                                                    </v-btn>
                                                </template>
                                                <v-date-picker
                                                    :value="item.payment_receipt_uploaded_at ? $date(item.payment_receipt_uploaded_at).format('YYYY-MM-DD') : null"
                                                    @input="updatePTPaymentDate(item, $event)"
                                                    no-title
                                                    scrollable
                                                ></v-date-picker>
                                            </v-menu>
                                            <v-tooltip v-if="!item.payment_receipt_uploaded_at" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon color="success" @click="setPTPaymentDateToday(item)" v-bind="attrs" v-on="on">
                                                        <v-icon x-small>mdi-check</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Mark as paid today</span>
                                            </v-tooltip>
                                        </div>
                                        <div v-else style="text-align:center;">
                                            <span class="grey--text">-</span>
                                        </div>
                                    </template>
                                    <template #[`item.status`]="{ item }">
                                        <div class="d-flex align-center">
                                            <v-chip small :color="item.deleted_at ? 'grey' : getStatusColor(item.status)" text-color="white" label>
                                                {{ item.deleted_at ? 'deleted' : item.status }}
                                            </v-chip>
                                            <v-tooltip v-if="item.status === 'pending' && !item.deleted_at" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon color="success" class="ml-1" @click="openPTApproveDialog(item)" v-bind="attrs" v-on="on">
                                                        <v-icon x-small>mdi-check</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Approve</span>
                                            </v-tooltip>
                                            <v-tooltip v-if="item.status === 'approved' && !item.deleted_at && canDeleteSubmissions" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn x-small icon color="warning" class="ml-1" @click="confirmPTUnapprove(item)" v-bind="attrs" v-on="on">
                                                        <v-icon x-small>mdi-undo</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Unapprove</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                    <template #[`item.approved_amount`]="{ item }">
                                        <span v-if="item.approved_amount" style="color: #4caf50; font-weight: 600;">
                                            {{ item.currency || 'USD' }} {{ formatCurrency(item.approved_amount) }}
                                        </span>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.dollar_rate_display`]="{ item }">
                                        <div v-if="item.currency === 'USD' && item.status !== 'denied' && !item.deleted_at" class="d-flex align-center">
                                            <v-edit-dialog
                                                :return-value.sync="item.dollar_rate"
                                                @save="savePTDollarRate(item)"
                                                large
                                                persistent
                                            >
                                                <span class="editable-field">₱{{ formatCurrency(item.submission_dollar_rate) }}</span>
                                                <template v-slot:input>
                                                    <v-text-field
                                                        v-model.number="editingPTDollarRate"
                                                        label="Dollar Rate"
                                                        type="number"
                                                        prefix="₱"
                                                        single-line
                                                        dense
                                                        @focus="editingPTDollarRate = item.submission_dollar_rate"
                                                    ></v-text-field>
                                                </template>
                                            </v-edit-dialog>
                                        </div>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.php_rate`]="{ item }">
                                        <span v-if="item.status !== 'denied' && !item.deleted_at"
                                            style="font-weight: 600; color: #1976d2">₱{{ formatCurrency(item.php_rate) }}</span>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.transfer_fee_display`]="{ item }">
                                        <div v-if="item.status !== 'denied' && !item.deleted_at" 
                                            class="d-flex align-center">
                                            <v-edit-dialog
                                                :return-value.sync="item.transfer_fee"
                                                @save="savePTTransferFee(item)"
                                                large
                                                persistent
                                            >
                                                <span class="editable-field">₱{{ formatCurrency(item.submission_transfer_fee) }}</span>
                                                <template v-slot:input>
                                                    <v-text-field
                                                        v-model.number="editingPTTransferFee"
                                                        label="Transfer Fee"
                                                        type="number"
                                                        prefix="₱"
                                                        single-line
                                                        dense
                                                        @focus="editingPTTransferFee = item.submission_transfer_fee"
                                                    ></v-text-field>
                                                </template>
                                            </v-edit-dialog>
                                        </div>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template #[`item.total_salary_expense`]="{ item }">
                                        <span v-if="item.status !== 'denied' && !item.deleted_at"
                                            style="font-weight: 700; color: #388e3c">₱{{ formatCurrency(item.total_salary_expense) }}</span>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                </v-data-table>

                                <!-- PT Summary (all filtered data — server-side totals) -->
                                <v-row class="mt-4" v-if="serverPTTotals.total_salary_expense > 0 || serverPTTotals.total_usd_approved > 0">
                                    <v-col cols="12">
                                        <v-alert type="info" outlined dense color="deep-purple">
                                            <v-row>
                                                <v-col cols="12" md="3">
                                                    <strong>Total USD Approved:</strong>
                                                    ${{ formatCurrency(totalPTUsdApproved) }}
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <strong>Total PHP Rate:</strong>
                                                    ₱{{ formatCurrency(totalPTPhpRate) }}
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <strong>Total Transfer Fees:</strong>
                                                    ₱{{ formatCurrency(totalPTTransferFees) }}
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <strong>Total Salary Expense:</strong>
                                                    <span style="font-weight: 700; color: #388e3c">₱{{
                                                        formatCurrency(totalPTSalaryExpense) }}</span>
                                                </v-col>
                                            </v-row>
                                            <div class="text-caption grey--text mt-1">(all filtered results)</div>
                                        </v-alert>
                                    </v-col>
                                </v-row>

                        <div v-if="paginatedPTSubmissionsWithExpense.length === 0 && !isLoadingPaginatedPTSubmissions"
                                    class="text-center py-8">
                                    <v-icon size="64" color="grey lighten-1">mdi-briefcase-outline</v-icon>
                                    <div class="mt-2 grey--text">No PT submissions found</div>
                                </div>
                            </v-card-text>
                        </v-tab-item>
                    </v-tabs-items>
                </v-tab-item>

                <!-- VA Profiles & Rates Tab -->
                <v-tab-item v-if="user && user.id === 122 && !ptOnlyMode">
                    <v-card-title>
                        <span style="font-size: 1.3rem; font-weight: 600">
                            VA Profiles & Rates Management (Daniza only)
                        </span>
                        <v-spacer></v-spacer>
                        <v-text-field v-model="userSearch" dense outlined hide-details placeholder="Search users..."
                            prepend-inner-icon="mdi-magnify" style="max-width: 300px" clearable></v-text-field>
                    </v-card-title>
                    <v-card-text>
                        <v-progress-linear v-if="isLoadingVAProfiles" indeterminate color="primary" class="mb-4"></v-progress-linear>
                        <v-data-table v-else :headers="vaHeaders" :items="usersWithRates" :search="userSearch"
                            :items-per-page="-1" hide-default-footer class="elevation-0">
                            <template #[`item.avatar`]="{ item }">
                                <v-avatar size="32" color="primary">
                                    <span class="white--text text-caption">
                                        {{ getInitials(item.name) }}
                                    </span>
                                </v-avatar>
                            </template>
                            <template #[`item.hourly_rate`]="{ item }">
                                <v-text-field v-model.number="item.hourly_rate" dense outlined hide-details
                                    type="number" min="0" step="0.01" style="max-width: 120px"
                                    @change="updateUserRate(item)"></v-text-field>
                            </template>
                            <template #[`item.payment_currency`]="{ item }">
                                <v-select v-model="item.payment_currency" :items="currencies" dense outlined
                                    hide-details style="max-width: 100px" @change="updateUserRate(item)"></v-select>
                            </template>
                            <template #[`item.actions`]="{ item }">
                                <v-btn icon small color="primary" @click="saveUserRate(item)">
                                    <v-icon small>mdi-content-save</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-tab-item>

                <!-- Exceptions Tab -->
                <v-tab-item v-if="!ptOnlyMode">
                    <v-card-title>
                        <span style="font-size: 1.3rem; font-weight: 600">
                            System-Flagged Exceptions
                        </span>
                    </v-card-title>

                    <!-- Filters Row -->
                    <v-card-text class="pb-0">
                        <v-row dense align="center">
                            <v-col cols="12" sm="6" md="2">
                                <v-select v-model="exceptionResolvedFilter" :items="exceptionResolvedOptions" dense outlined hide-details label="Status"></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="2">
                                <v-select v-model="exceptionSourceFilter" :items="exceptionSourceOptions" dense outlined hide-details label="Source"></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-select v-model="exceptionUserFilter" :items="exceptionUserOptions" dense outlined hide-details label="User" clearable></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="2">
                                <v-select v-model="exceptionTypeFilter" :items="exceptionTypeOptions" dense outlined hide-details label="Type" clearable></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="3" class="text-right">
                                <span class="text-caption grey--text">{{ filteredExceptions.length }} of {{ exceptions.length }} exceptions</span>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-card-text>
                        <v-progress-linear v-if="isLoadingExceptions" indeterminate color="primary"
                            class="mb-4"></v-progress-linear>
                        <template v-else>
                            <v-alert v-for="exception in filteredExceptions" :key="exception.id"
                                :type="exception.is_resolved ? 'success' : (exception.severity === 'error' ? 'error' : 'warning')" outlined class="mb-3">
                                <v-row align="center">
                                    <v-col cols="12" md="6">
                                        <div style="font-weight: 600">
                                            {{ exception.user?.name || '-' }} - {{ exception.invoice_number || exception.invoiceNumber || '-' }}
                                        </div>
                                        <div>{{ formatExceptionDescription(exception.description) }}</div>
                                        <div class="text-caption grey--text">
                                            <v-icon x-small>mdi-clock-outline</v-icon>
                                            Detected: {{ formatDateTime(exception.detected_at || exception.detectedAt) }}
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="3" class="text-center">
                                        <v-chip small :color="exception.pt_submission_id ? 'deep-purple' : 'primary'" text-color="white" class="mr-1">
                                            {{ exception.pt_submission_id ? 'PT Submission' : 'Regular Submission' }}
                                        </v-chip>
                                        <v-chip small :color="getExceptionTypeColor(exception.exception_type)" text-color="white">
                                            {{ formatExceptionType(exception.exception_type) }}
                                        </v-chip>
                                    </v-col>
                                    <v-col cols="12" md="3">
                                        <div class="d-flex flex-column align-end">
                                            <v-checkbox
                                                :input-value="exception.is_resolved"
                                                @change="toggleExceptionResolved(exception, $event)"
                                                :label="exception.is_resolved ? 'Resolved' : 'Unresolved'"
                                                dense
                                                hide-details
                                                class="mt-0 pt-0"
                                                :color="exception.is_resolved ? 'success' : 'grey'"
                                            ></v-checkbox>
                                            <div v-if="exception.is_resolved && exception.resolver" class="text-caption grey--text mt-1">
                                                by {{ exception.resolver.name }}
                                            </div>
                                            <v-text-field
                                                :value="exception.resolution_notes || ''"
                                                @change="updateExceptionNotes(exception, $event)"
                                                dense
                                                hide-details
                                                placeholder="Resolution notes..."
                                                style="font-size: 12px; max-width: 250px;"
                                                class="mt-1"
                                            ></v-text-field>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-alert>
                            <div v-if="filteredExceptions.length === 0" class="text-center py-8">
                                <v-icon size="64" color="success">mdi-check-circle</v-icon>
                                <div class="mt-2 grey--text">No exceptions found matching filters</div>
                            </div>
                        </template>
                    </v-card-text>
                </v-tab-item>
            </v-tabs-items>
        </v-card>

        <!-- Approve Dialog -->
        <v-dialog v-model="approveDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon left color="success">mdi-check-circle</v-icon>
                    Approve Submission
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <p>
                        You are approving the submission from
                        <strong>{{ selectedSubmission?.user?.name }}</strong> for invoice
                        <strong>{{ selectedSubmission?.invoice_number }}</strong>.
                    </p>
                    <v-text-field v-model.number="approvedAmount" label="Approved Amount *" outlined dense type="number"
                        min="0" step="0.01" :prefix="selectedSubmission?.currency"
                        :rules="[(v) => !!v || 'Amount is required']"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeApproveDialog" class="text-none">
                        Cancel
                    </v-btn>
                    <v-btn color="success" @click="confirmApprove" :disabled="!approvedAmount" class="text-none">
                        Approve
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Deny Dialog -->
        <v-dialog v-model="denyDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon left color="error">mdi-close-circle</v-icon>
                    Deny Submission
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <p>
                        You are denying the submission from
                        <strong>{{ selectedSubmission?.user?.name }}</strong> for invoice
                        <strong>{{ selectedSubmission?.invoice_number }}</strong>.
                    </p>
                    <v-textarea v-model="denyReason" label="Reason for Denial *" outlined dense rows="3"
                        :rules="[(v) => !!v || 'Reason is required']"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDenyDialog" class="text-none">
                        Cancel
                    </v-btn>
                    <v-btn color="error" @click="confirmDeny" :disabled="!denyReason" class="text-none">
                        Deny
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Upload Receipt Dialog -->
        <v-dialog v-model="receiptDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon left color="primary">mdi-upload</v-icon>
                    Upload Payment Receipt
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <p>
                        Upload payment proof for
                        <strong>{{ selectedSubmission?.user?.name }}</strong> - Invoice
                        <strong>{{ selectedSubmission?.invoice_number }}</strong>.
                    </p>
                    <v-file-input v-model="receiptFile" label="Payment Receipt *" outlined dense prepend-icon=""
                        prepend-inner-icon="mdi-file-upload" show-size
                        :rules="[(v) => !!v || 'Receipt file is required']"></v-file-input>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeReceiptDialog" class="text-none">
                        Cancel
                    </v-btn>
                    <v-btn color="primary" @click="confirmUploadReceipt" :disabled="!receiptFile" class="text-none">
                        Upload
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Export Invoices Confirmation Dialog -->
        <v-dialog v-model="exportInvoicesDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon left color="primary">mdi-download-multiple</v-icon>
                    Export Invoice Files
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <p>
                        This will download all invoice files from the filtered submissions.
                    </p>
                    <v-alert type="info" dense text class="mt-2">
                        <div class="text-body-2">
                            <strong>{{paginatedSubmissions.filter(s => s.invoice_file_path).length}}</strong> invoice
                            files will
                            be
                            downloaded.
                        </div>
                        <div v-if="dateRange && dateRange.length === 2" class="text-caption mt-1">
                            Date Range: {{ formattedDateRange }}
                        </div>
                        <div v-else class="text-caption mt-1">
                            All submissions (no date filter applied)
                        </div>
                    </v-alert>
                    <p class="text-caption grey--text mt-3 mb-0">
                        Note: Multiple files will be downloaded sequentially. Please allow pop-ups if prompted by your
                        browser.
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeExportInvoicesDialog" class="text-none">
                        Cancel
                    </v-btn>
                    <v-btn color="primary" @click="confirmExportInvoices" class="text-none">
                        <v-icon left small>mdi-download</v-icon>
                        Download Files
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Export PT Invoices Confirmation Dialog -->
        <v-dialog v-model="exportPTInvoicesDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon left color="primary">mdi-download-multiple</v-icon>
                    Export PT Invoice Files
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <p>
                        This will download all invoice files from the filtered PT submissions.
                    </p>
                    <v-alert type="info" dense text class="mt-2">
                        <div class="text-body-2">
                            <strong>{{paginatedPTSubmissions.filter(s => s.invoice_file_path).length}}</strong> invoice
                            files will
                            be
                            downloaded.
                        </div>
                        <div v-if="ptDateRange && ptDateRange.length === 2" class="text-caption mt-1">
                            Date Range: {{ formattedPTDateRange }}
                        </div>
                        <div v-else class="text-caption mt-1">
                            All submissions (no date filter applied)
                        </div>
                    </v-alert>
                    <p class="text-caption grey--text mt-3 mb-0">
                        Note: Multiple files will be downloaded sequentially. Please allow pop-ups if prompted by your
                        browser.
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeExportPTInvoicesDialog" class="text-none">
                        Cancel
                    </v-btn>
                    <v-btn color="primary" @click="confirmExportPTInvoices" class="text-none">
                        <v-icon left small>mdi-download</v-icon>
                        Download Files
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="450px" persistent>
            <v-card>
                <v-card-title class="error white--text">
                    <v-icon left color="white">mdi-delete-alert</v-icon>
                    Delete Submission
                </v-card-title>
                <v-card-text class="pt-4">
                    <p>Are you sure you want to delete this submission?</p>
                    <div v-if="submissionToDelete" class="mt-3">
                        <div><strong>Invoice #:</strong> {{ submissionToDelete.invoice_number }}</div>
                        <div><strong>Employee:</strong> {{ submissionToDelete.user?.name }}</div>
                        <div><strong>Amount:</strong> {{ submissionToDelete.currency }} {{ submissionToDelete.invoice_amount }}</div>
                    </div>
                    <v-alert type="info" dense text class="mt-4 mb-0">
                        You can restore deleted submissions later.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelDelete" class="text-none">Cancel</v-btn>
                    <v-btn color="error" @click="deleteSubmission" class="text-none">
                        <v-icon left small>mdi-delete</v-icon>
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Restore Confirmation Dialog -->
        <v-dialog v-model="restoreDialog" max-width="450px" persistent>
            <v-card>
                <v-card-title class="success white--text">
                    <v-icon left color="white">mdi-restore</v-icon>
                    Restore Submission
                </v-card-title>
                <v-card-text class="pt-4">
                    <p>Are you sure you want to restore this submission?</p>
                    <div v-if="submissionToRestore" class="mt-3">
                        <div><strong>Invoice #:</strong> {{ submissionToRestore.invoice_number }}</div>
                        <div><strong>Employee:</strong> {{ submissionToRestore.user?.name }}</div>
                        <div><strong>Amount:</strong> {{ submissionToRestore.currency }} {{ submissionToRestore.invoice_amount }}</div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelRestore" class="text-none">Cancel</v-btn>
                    <v-btn color="success" @click="restoreSubmission" class="text-none">
                        <v-icon left small>mdi-restore</v-icon>
                        Restore
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Unapprove Confirmation Dialog -->
        <v-dialog v-model="unapproveDialog" max-width="450px" persistent>
            <v-card>
                <v-card-title class="warning white--text">
                    <v-icon left color="white">mdi-undo</v-icon>
                    Unapprove Submission
                </v-card-title>
                <v-card-text class="pt-4">
                    <p>Are you sure you want to unapprove this submission? It will be set back to pending status.</p>
                    <div v-if="submissionToUnapprove" class="mt-3">
                        <div><strong>Invoice #:</strong> {{ submissionToUnapprove.invoice_number }}</div>
                        <div><strong>Employee:</strong> {{ submissionToUnapprove.user?.name }}</div>
                        <div><strong>Approved Amount:</strong> {{ submissionToUnapprove.currency }} {{ submissionToUnapprove.approved_amount }}</div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelUnapprove" class="text-none">Cancel</v-btn>
                    <v-btn color="warning" @click="unapproveSubmission" class="text-none">
                        <v-icon left small>mdi-undo</v-icon>
                        Unapprove
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- PT Approve Dialog -->
        <v-dialog v-model="ptApproveDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon left color="success">mdi-check-circle</v-icon>
                    Approve PT Submission
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <p>
                        You are approving the PT submission from
                        <strong>{{ selectedPTSubmission?.user?.name }}</strong> for invoice
                        <strong>{{ selectedPTSubmission?.invoice_number }}</strong>.
                    </p>
                    <p class="text-caption grey--text">
                        Client: {{ selectedPTSubmission?.client_name }}
                    </p>
                    <v-text-field v-model.number="ptApprovedAmount" label="Approved Amount *" outlined dense type="number"
                        min="0" step="0.01" :prefix="selectedPTSubmission?.currency || 'USD'"
                        :rules="[(v) => !!v || 'Amount is required']"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closePTApproveDialog" class="text-none">
                        Cancel
                    </v-btn>
                    <v-btn color="success" @click="confirmPTApprove" :disabled="!ptApprovedAmount" class="text-none">
                        Approve
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- PT Deny Dialog -->
        <v-dialog v-model="ptDenyDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon left color="error">mdi-close-circle</v-icon>
                    Deny PT Submission
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <p>
                        You are denying the PT submission from
                        <strong>{{ selectedPTSubmission?.user?.name }}</strong> for invoice
                        <strong>{{ selectedPTSubmission?.invoice_number }}</strong>.
                    </p>
                    <p class="text-caption grey--text">
                        Client: {{ selectedPTSubmission?.client_name }}
                    </p>
                    <v-textarea v-model="ptDenyReason" label="Reason for Denial *" outlined dense rows="3"
                        :rules="[(v) => !!v || 'Reason is required']"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closePTDenyDialog" class="text-none">
                        Cancel
                    </v-btn>
                    <v-btn color="error" @click="confirmPTDeny" :disabled="!ptDenyReason" class="text-none">
                        Deny
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- PT Delete Confirmation Dialog -->
        <v-dialog v-model="ptDeleteDialog" max-width="450px" persistent>
            <v-card>
                <v-card-title class="error white--text">
                    <v-icon left color="white">mdi-delete</v-icon>
                    Delete PT Submission
                </v-card-title>
                <v-card-text class="pt-4">
                    <p>Are you sure you want to delete this PT submission? This action can be undone.</p>
                    <div v-if="ptSubmissionToDelete" class="mt-3">
                        <div><strong>Invoice #:</strong> {{ ptSubmissionToDelete.invoice_number }}</div>
                        <div><strong>Employee:</strong> {{ ptSubmissionToDelete.user?.name }}</div>
                        <div><strong>Client:</strong> {{ ptSubmissionToDelete.client_name }}</div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelPTDelete" class="text-none">Cancel</v-btn>
                    <v-btn color="error" @click="deletePTSubmission" class="text-none">
                        <v-icon left small>mdi-delete</v-icon>
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- PT Restore Confirmation Dialog -->
        <v-dialog v-model="ptRestoreDialog" max-width="450px" persistent>
            <v-card>
                <v-card-title class="success white--text">
                    <v-icon left color="white">mdi-restore</v-icon>
                    Restore PT Submission
                </v-card-title>
                <v-card-text class="pt-4">
                    <p>Are you sure you want to restore this PT submission?</p>
                    <div v-if="ptSubmissionToRestore" class="mt-3">
                        <div><strong>Invoice #:</strong> {{ ptSubmissionToRestore.invoice_number }}</div>
                        <div><strong>Employee:</strong> {{ ptSubmissionToRestore.user?.name }}</div>
                        <div><strong>Client:</strong> {{ ptSubmissionToRestore.client_name }}</div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelPTRestore" class="text-none">Cancel</v-btn>
                    <v-btn color="success" @click="restorePTSubmission" class="text-none">
                        <v-icon left small>mdi-restore</v-icon>
                        Restore
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- PT Unapprove Confirmation Dialog -->
        <v-dialog v-model="ptUnapproveDialog" max-width="450px" persistent>
            <v-card>
                <v-card-title class="warning white--text">
                    <v-icon left color="white">mdi-undo</v-icon>
                    Unapprove PT Submission
                </v-card-title>
                <v-card-text class="pt-4">
                    <p>Are you sure you want to unapprove this PT submission? It will be set back to pending status.</p>
                    <div v-if="ptSubmissionToUnapprove" class="mt-3">
                        <div><strong>Invoice #:</strong> {{ ptSubmissionToUnapprove.invoice_number }}</div>
                        <div><strong>Employee:</strong> {{ ptSubmissionToUnapprove.user?.name }}</div>
                        <div><strong>Client:</strong> {{ ptSubmissionToUnapprove.client_name }}</div>
                        <div><strong>Approved Amount:</strong> USD {{ ptSubmissionToUnapprove.approved_amount }}</div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelPTUnapprove" class="text-none">Cancel</v-btn>
                    <v-btn color="warning" @click="unapprovePTSubmission" class="text-none">
                        <v-icon left small>mdi-undo</v-icon>
                        Unapprove
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Invoice PDF Upload Dialog (normal + PT) -->
        <v-dialog v-model="invoiceUploadDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon left color="white">mdi-file-pdf-box</v-icon>
                    {{ invoiceUploadSubmission && invoiceUploadSubmission.invoice_file_path ? 'Replace' : 'Upload' }} Invoice PDF
                </v-card-title>
                <v-card-text class="pt-4">
                    <p v-if="invoiceUploadSubmission" class="mb-3">
                        Upload a corrected invoice for <strong>{{ invoiceUploadSubmission.user?.name }}</strong>
                        - Invoice #{{ invoiceUploadSubmission.invoice_number }}
                    </p>
                    <p v-if="invoiceUploadSubmission && invoiceUploadSubmission.invoice_file_path" class="text-caption grey--text mb-3">
                        This will replace the currently linked invoice.
                    </p>
                    <v-file-input
                        v-model="invoiceUploadFile"
                        label="Select PDF"
                        accept="application/pdf,.pdf"
                        prepend-icon="mdi-paperclip"
                        outlined
                        dense
                        show-size
                        :rules="[v => !v || v.size <= 20971520 || 'File must be less than 20MB', v => !v || /\.pdf$/i.test(v.name) || 'File must be a PDF']"
                    ></v-file-input>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeInvoiceUploadDialog" class="text-none">Cancel</v-btn>
                    <v-btn color="primary" @click="confirmInvoiceUpload" :disabled="!invoiceUploadFile || !/\.pdf$/i.test(invoiceUploadFile.name)" class="text-none">
                        <v-icon left small>mdi-upload</v-icon>
                        Upload
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Additional Document Upload Dialog (normal + PT) -->
        <v-dialog v-model="ptAdditionalDocDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="teal white--text">
                    <v-icon left color="white">mdi-file-upload</v-icon>
                    Upload Additional Document
                </v-card-title>
                <v-card-text class="pt-4">
                    <p v-if="ptAdditionalDocSubmission" class="mb-3">
                        Upload an additional document for <strong>{{ ptAdditionalDocSubmission.user?.name }}</strong>
                        - Invoice #{{ ptAdditionalDocSubmission.invoice_number }}
                    </p>
                    <v-file-input
                        v-model="ptAdditionalDocFile"
                        label="Select file"
                        prepend-icon="mdi-paperclip"
                        outlined
                        dense
                        show-size
                        :rules="[v => !v || v.size <= 20971520 || 'File must be less than 20MB']"
                    ></v-file-input>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closePTAdditionalDocDialog" class="text-none">Cancel</v-btn>
                    <v-btn color="teal" dark @click="confirmPTAdditionalDocUpload" :disabled="!ptAdditionalDocFile" class="text-none">
                        <v-icon left small>mdi-upload</v-icon>
                        Upload
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- View Details Dialog -->
        <!-- Tracked-time breakdown by app (Classic / TD2 / DGTasks) -->
        <v-dialog v-model="timeBreakdownDialog" max-width="800px">
            <v-card>
                <v-card-title>
                    <v-icon left color="primary">mdi-chart-timeline-variant</v-icon>
                    Tracked Time Breakdown
                    <v-spacer></v-spacer>
                    <v-btn icon @click="timeBreakdownDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-subtitle v-if="timeBreakdownSubmission">
                    {{ timeBreakdown?.user?.name || '' }}
                    &middot; {{ formatDateRange(timeBreakdownSubmission.invoice_period_from, timeBreakdownSubmission.invoice_period_to) }}
                </v-card-subtitle>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <div v-if="timeBreakdownLoading" class="text-center py-6">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <div class="mt-2 grey--text">Loading Classic, TD2 and DGTasks time...</div>
                    </div>
                    <v-alert v-else-if="timeBreakdownError" type="error" dense text>{{ timeBreakdownError }}</v-alert>
                    <template v-else-if="timeBreakdown">
                        <v-row dense>
                            <v-col v-for="st in timeBreakdown.source_totals" :key="st.source" cols="12" sm="4">
                                <v-card outlined class="pa-3 text-center">
                                    <v-chip x-small label :color="breakdownSourceColor(st.source)">{{ breakdownSourceLabel(st.source) }}</v-chip>
                                    <div class="text-h6 mt-1">{{ st.formatted }}</div>
                                    <div class="text-caption grey--text">{{ breakdownPercent(st.seconds) }}% of total</div>
                                </v-card>
                            </v-col>
                        </v-row>
                        <div class="d-flex mt-3 text-body-2">
                            <div><strong>Total now:</strong> {{ timeBreakdown.total_formatted }}</div>
                            <v-spacer></v-spacer>
                            <div v-if="timeBreakdown.submitted_hours !== null">
                                <strong>TD saved at submission:</strong> {{ formatHours(timeBreakdown.submitted_hours) }}
                            </div>
                        </div>
                        <v-alert v-if="breakdownDiffers" type="info" dense text class="mt-2 mb-0">
                            The total now differs from the figure saved when the employee submitted (time added or edited since, or the submission predates a source being merged in).
                        </v-alert>
                        <div class="text-caption grey--text mt-2">
                            Time tracked on more than one app at the same moment is counted once (all apps compared on the Time Doctor clock), so it only shows under one source.
                        </div>
                        <v-alert v-for="w in timeBreakdown.warnings" :key="w" type="warning" dense text class="mt-2 mb-0">{{ w }}</v-alert>

                        <v-simple-table dense class="mt-4">
                            <thead>
                                <tr>
                                    <th>Source</th>
                                    <th>Project</th>
                                    <th>Task</th>
                                    <th class="text-right">Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, i) in timeBreakdown.items" :key="i">
                                    <td><v-chip x-small label :color="breakdownSourceColor(row.source)">{{ breakdownSourceLabel(row.source) }}</v-chip></td>
                                    <td>{{ row.project_name }}</td>
                                    <td>{{ row.task_name }}</td>
                                    <td class="text-right">{{ row.formatted_time }}</td>
                                </tr>
                                <tr v-if="!timeBreakdown.items.length">
                                    <td colspan="4" class="grey--text text-center">No tracked time in this period</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </template>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="detailsDialog" max-width="600px">
            <v-card v-if="selectedSubmission">
                <v-card-title>
                    <v-icon left>mdi-file-document</v-icon>
                    Submission Details
                    <v-spacer></v-spacer>
                    <v-btn icon @click="detailsDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <v-simple-table>
                        <tbody>
                            <tr>
                                <td style="font-weight: 600; width: 40%">Employee</td>
                                <td>{{ selectedSubmission.user.name }}</td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Email</td>
                                <td>{{ selectedSubmission.user.email }}</td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Invoice Number</td>
                                <td>{{ selectedSubmission.invoice_number }}</td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Company</td>
                                <td>{{ selectedSubmission.company.name }}</td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Service Period</td>
                                <td>
                                    {{ formatDateRange(selectedSubmission.invoice_period_from,
                                        selectedSubmission.invoice_period_to) }}
                                </td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Hours Worked</td>
                                <td>
                                    {{ formatHours(selectedSubmission.hours_worked) }}
                                </td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Invoice Amount</td>
                                <td>
                                    {{ selectedSubmission.currency }}
                                    {{ formatCurrency(selectedSubmission.invoice_amount) }}
                                </td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Status</td>
                                <td>
                                    <v-chip small :color="getStatusColor(selectedSubmission.status)" text-color="white"
                                        label>
                                        {{ selectedSubmission.status }}
                                    </v-chip>
                                </td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Submitted</td>
                                <td>{{ formatDateTime(selectedSubmission.submitted_at) }}</td>
                            </tr>
                            <tr v-if="selectedSubmission.approved_amount">
                                <td style="font-weight: 600">Approved Amount</td>
                                <td>
                                    <span style="color: #4caf50; font-weight: 600;">
                                        {{ selectedSubmission.currency }} {{
                                            formatCurrency(selectedSubmission.approved_amount)
                                        }}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Invoice File</td>
                                <td>
                                    <v-chip small outlined v-if="selectedSubmission.invoice_file_path">
                                        <v-icon left x-small>mdi-file-pdf-box</v-icon>
                                        <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.invoice_file_path}`"
                                            target="_blank" style="text-decoration: none; color: inherit;">
                                            {{ selectedSubmission.invoice_file_path.split('/').pop() }}
                                        </a>
                                    </v-chip>
                                    <span v-else class="grey--text">Not uploaded</span>
                                </td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">COR File</td>
                                <td>
                                    <v-chip small outlined v-if="selectedSubmission.cor_file_path">
                                        <v-icon left x-small>mdi-certificate</v-icon>
                                        <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.cor_file_path}`"
                                            target="_blank" style="text-decoration: none; color: inherit;">
                                            {{ selectedSubmission.cor_file_path.split('/').pop() }}
                                        </a>
                                    </v-chip>
                                    <span v-else class="grey--text">Not uploaded</span>
                                </td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Acknowledgement Receipt</td>
                                <td>
                                    <v-chip small outlined v-if="selectedSubmission.acknowledgement_receipt_path">
                                        <v-icon left x-small>mdi-receipt-text</v-icon>
                                        <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.acknowledgement_receipt_path}`"
                                            target="_blank" style="text-decoration: none; color: inherit;">
                                            {{ selectedSubmission.acknowledgement_receipt_path.split('/').pop() }}
                                        </a>
                                    </v-chip>
                                    <span v-else class="grey--text">Not uploaded</span>
                                </td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Tax Return Files</td>
                                <td>
                                    <div
                                        v-if="selectedSubmission.tax_return_urls && selectedSubmission.tax_return_urls.length > 0">
                                        <v-chip small outlined
                                            v-for="(taxFile, index) in selectedSubmission.tax_return_urls" :key="index"
                                            class="mr-1 mb-1">
                                            <v-icon left x-small>mdi-file-document</v-icon>
                                            <a :href="`https://api.dgventureslimited.com/storage/${taxFile.path}`"
                                                target="_blank" style="text-decoration: none; color: inherit;">
                                                {{ taxFile.filename }}
                                            </a>
                                        </v-chip>
                                    </div>
                                    <span v-else class="grey--text">Not uploaded</span>
                                </td>
                            </tr>
                            <tr>
                                <td style="font-weight: 600">Payment Receipt</td>
                                <td>
                                    <v-chip small outlined v-if="selectedSubmission.payment_receipt_path">
                                        <v-icon left x-small>mdi-receipt</v-icon>
                                        <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.payment_receipt_path}`"
                                            target="_blank" style="text-decoration: none; color: inherit;">
                                            {{ selectedSubmission.payment_receipt_path.split('/').pop() }}
                                        </a>
                                    </v-chip>
                                    <span v-else class="grey--text">Not uploaded</span>
                                </td>
                            </tr>
                            <tr v-if="selectedSubmission.comments">
                                <td style="font-weight: 600">Comments</td>
                                <td>{{ selectedSubmission.comments }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card-text>
            </v-card>
        </v-dialog>
        <!-- Submission Exceptions Dialog -->
        <v-dialog v-model="exceptionsDialog" max-width="650px">
            <v-card v-if="exceptionsSubmission">
                <v-card-title>
                    <v-icon left color="orange">mdi-alert</v-icon>
                    Exceptions - {{ exceptionsSubmission.user?.name }}
                    <v-spacer></v-spacer>
                    <v-btn icon @click="exceptionsDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-subtitle class="pb-0">
                    Invoice: <strong>{{ exceptionsSubmission.invoice_number }}</strong>
                </v-card-subtitle>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <v-alert v-for="exception in exceptionsSubmission.exceptions" :key="exception.id"
                        :type="exception.is_resolved ? 'success' : (exception.severity === 'error' ? 'error' : exception.severity === 'warning' ? 'warning' : 'info')" outlined class="mb-3">
                        <v-row align="center" no-gutters>
                            <v-col>
                                <div style="font-weight: 600">
                                    {{ formatExceptionType(exception.exception_type) }}
                                </div>
                                <div>{{ formatExceptionDescription(exception.description) }}</div>
                                <div v-if="exception.expected_value || exception.actual_value" class="mt-1">
                                    <span v-if="exception.expected_value" class="text-caption">Expected: <strong>{{ exception.expected_value }}</strong></span>
                                    <span v-if="exception.expected_value && exception.actual_value" class="mx-1">|</span>
                                    <span v-if="exception.actual_value" class="text-caption">Actual: <strong>{{ exception.actual_value }}</strong></span>
                                </div>
                                <div class="text-caption grey--text mt-1">
                                    <v-icon x-small>mdi-clock-outline</v-icon>
                                    Detected: {{ formatDateTime(exception.detected_at) }}
                                    <template v-if="exception.is_resolved && exception.resolver">
                                        <span class="ml-2">| Resolved by {{ exception.resolver.name }}</span>
                                    </template>
                                </div>
                            </v-col>
                            <v-col cols="auto" class="ml-2">
                                <div class="d-flex flex-column align-end">
                                    <v-checkbox
                                        :input-value="exception.is_resolved"
                                        @change="toggleExceptionResolved(exception, $event)"
                                        :label="exception.is_resolved ? 'Resolved' : 'Unresolved'"
                                        dense
                                        hide-details
                                        class="mt-0 pt-0"
                                        :color="exception.is_resolved ? 'success' : 'grey'"
                                    ></v-checkbox>
                                    <v-text-field
                                        :value="exception.resolution_notes || ''"
                                        @change="updateExceptionNotes(exception, $event)"
                                        dense
                                        hide-details
                                        placeholder="Resolution notes..."
                                        style="font-size: 12px; max-width: 220px;"
                                        class="mt-1"
                                    ></v-text-field>
                                </div>
                            </v-col>
                        </v-row>
                    </v-alert>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Change Currency & Amount Dialog -->
        <v-dialog v-model="currencyDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon left color="primary">mdi-pencil</v-icon>
                    Edit Invoice Details
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <p>
                        Update details for invoice
                        <strong>{{ currencySubmission?.invoice_number }}</strong>
                        from <strong>{{ currencySubmission?.user?.name }}</strong>.
                    </p>
                    <v-select v-model="newCurrency" :items="currencies" label="Currency *" outlined dense
                        :rules="[(v) => !!v || 'Currency is required']"></v-select>
                    <v-text-field v-model.number="newInvoiceAmount" label="Invoice Amount *" outlined dense type="number"
                        min="0" step="0.01" :prefix="newCurrency || 'USD'"
                        :rules="[(v) => !!v || 'Amount is required']"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeCurrencyDialog" class="text-none">Cancel</v-btn>
                    <v-btn color="primary" @click="confirmCurrencyChange" :disabled="!newCurrency || !newInvoiceAmount" class="text-none">
                        Update
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: "PayrollManagerView",
    props: {
        user: {
            type: Object,
            default: null,
        },
        users: {
            type: Array,
            default: () => [],
        },
        companies: {
            type: Array,
            default: () => [],
        },
        exceptions: {
            type: Array,
            default: () => [],
        },
        isLoadingExceptions: {
            type: Boolean,
            default: false,
        },
        payrollRate: {
            type: Object,
            default: () => ({ dollar_rate: 0, transfer_fee: 0 }),
        },
        isLoadingRate: {
            type: Boolean,
            default: false,
        },
        ptOnlyMode: {
            type: Boolean,
            default: false,
        },
        submissionsRefreshKey: {
            type: Number,
            default: 0,
        },
        ptSubmissionsRefreshKey: {
            type: Number,
            default: 0,
        },
        lastUpdatedSubmission: {
            type: Object,
            default: null,
        },
        lastUpdatedPTSubmission: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            activeTab: 0,
            submissionsViewTab: 0, // 0 = Normal, 1 = PT
            statusFilter: "all",
            userFilter: null,
            companyFilter: null,
            currencyFilter: "All",
            quarterFilter: "all",
            userSearch: "",
            currencies: ["USD", "PHP", "EUR", "GBP"],
            // Server-side pagination state — normal submissions
            paginatedSubmissions: [],
            submissionsTotal: 0,
            pendingCount: 0,
            submissionsOptions: { page: 1, itemsPerPage: 50 },
            submissionsSortBy: 'submitted_at',
            submissionsSortDesc: true,
            submissionsSortOptions: [
                { text: 'Timestamp', value: 'submitted_at' },
                { text: 'Employee', value: 'user_name' },
                { text: 'Company', value: 'company_name' },
                { text: 'Status', value: 'status' },
                { text: 'Invoice #', value: 'invoice_number' },
                { text: 'Invoice Amount', value: 'invoice_amount' },
                { text: 'Admin Comments', value: 'admin_comments' },
            ],
            isLoadingPaginatedSubmissions: false,
            // Server-side pagination state — PT submissions
            paginatedPTSubmissions: [],
            ptSubmissionsTotal: 0,
            ptPendingCount: 0,
            ptSubmissionsOptions: { page: 1, itemsPerPage: 50 },
            ptSubmissionsSortBy: 'submitted_at',
            ptSubmissionsSortDesc: true,
            isLoadingPaginatedPTSubmissions: false,
            vaProfiles: [],
            isLoadingVAProfiles: false,
            // PT status field options
            invoiceStatusOptions: [
                'YES (PT Accounts)',
                'Validated for Approval (Leah)',
                'ON HOLD',
                'YES (Daniza)',
                'VERIFYING',
                'NO Bank Details',
                'NO COR!',
                'NO A/R',
                'WITH COR',
                'WITH A/R',
            ],
            arStatusOptions: [
                'YES (PT Accounts)',
                'Validated for Approval (Leah)',
                'ON HOLD',
                'YES (Daniza)',
                'VERIFYING',
                'NO Bank Details',
                'NO COR!',
                'NO A/R',
                'WITH COR',
                'WITH A/R',
            ],
            bankDetailsOptions: [
                'Same',
                'NEW',
                'NONE',
                'ERROR',
            ],
            clientPaymentStatusOptions: [
                'NO PAYMENT YET',
                'CLIENT PAID',
                'PT Expense',
                'DG Ventures Expense',
                'PT Expense (Training)',
                'CLIENT NOT GOING TO PAY',
                'Revolution Doors/Windows',
                'Go Irish Tours',
                'Funeral Director Solutions',
            ],
            timeDoctorStatusOptions: [
                'Matches Invoice',
                'For Clarifications',
                'TIME DOCTOR VERIFIED',
                'NO TD record - needs approval from Manager',
                'NO NEED TD (as per client)',
            ],
            // Date range filter
            dateRangeMenu: false,
            dateRange: [],
            // Paid date range filter
            paidDateRangeMenu: false,
            paidDateRange: [],
            // Export dialogs
            exportInvoicesDialog: false,
            exportPTInvoicesDialog: false,
            // Submissions table headers
            submissionsHeaders: [
                { text: "Actions", value: "actions", sortable: false, width: "80px" },
                { text: "Timestamp", value: "submitted_at", sortable: false, width: "120px" },
                { text: "Employee", value: "user", sortable: false, width: "180px" },
                { text: "Invoice Service Period", value: "invoice_period", sortable: false, width: "140px" },
                { text: "Company", value: "company", sortable: false, width: "120px" },
                { text: "Invoice #", value: "invoice_number", sortable: false },
                { text: "Hours Worked", value: "hours_worked", sortable: false, align: "right", width: "120px" },
                { text: "Invoice Amount", value: "invoice_amount", sortable: false, align: "right", width: "120px" },
                { text: "Comments", value: "comments", sortable: false, width: "150px" },
                { text: "Admin Comments", value: "admin_comments", sortable: false, width: "200px" },
                { text: "Invoice (PDF)", value: "invoice_file_url", sortable: false, width: "100px" },
                { text: "COR (BIR 2303)", value: "cor_file_url", sortable: false, width: "100px" },
                { text: "Ack Receipt", value: "acknowledgement_receipt_url", sortable: false, width: "100px" },
                { text: "Tax Returns", value: "tax_return_urls", sortable: false, width: "100px" },
                { text: "Additional Doc", value: "additional_document_url", sortable: false, width: "120px" },
                { text: "Date Paid", value: "payment_receipt_uploaded_at", sortable: false, width: "100px" },
                { text: "Status", value: "status", sortable: false, width: "100px" },
                { text: "Approved Amount", value: "approved_amount", sortable: false, align: "right" },
                { text: "Dollar Rate", value: "dollar_rate_display", sortable: false, align: "right" },
                { text: "PHP Rate", value: "php_rate", sortable: false, align: "right" },
                { text: "Transfer Fee", value: "transfer_fee_display", sortable: false, align: "right" },
                { text: "Total Salary Expense", value: "total_salary_expense", sortable: false, align: "right" },
            ],
            // PT Submissions table headers
            ptSubmissionsHeaders: [
                { text: "Actions", value: "actions", sortable: false, width: "80px" },
                { text: "Timestamp", value: "submitted_at", sortable: false, width: "120px" },
                { text: "Employee", value: "user", sortable: false, width: "180px" },
                { text: "Client Name", value: "client_name", sortable: false, width: "150px" },
                { text: "Invoice Service Period", value: "invoice_period", sortable: false, width: "140px" },
                { text: "Company", value: "company", sortable: false, width: "120px" },
                { text: "Invoice #", value: "invoice_number", sortable: false },
                { text: "Hours Worked", value: "hours_worked", sortable: false, align: "right", width: "120px" },
                { text: "Invoice Amount", value: "invoice_amount", sortable: false, align: "right", width: "120px" },
                { text: "Invoice (PDF)", value: "invoice_file_url", sortable: false, width: "100px" },
                { text: "Capacity Form", value: "capacity_form_url", sortable: false, width: "100px" },
                { text: "Overtime Approval", value: "overtime_approval_url", sortable: false, width: "100px" },
                { text: "Employee DB Form", value: "employee_db_form_url", sortable: false, width: "100px" },
                { text: "Signature/NDA", value: "signature_ack_url", sortable: false, width: "100px" },
                { text: "Ack Receipt", value: "acknowledgement_receipt_url", sortable: false, width: "100px" },
                { text: "COR (BIR 2303)", value: "cor_file_url", sortable: false, width: "100px" },
                { text: "Tax Returns", value: "tax_return_urls", sortable: false, width: "100px" },
                { text: "Additional Doc", value: "additional_document_url", sortable: false, width: "120px" },
                { text: "Comments", value: "comments", sortable: false, width: "150px" },
                { text: "Admin Comments", value: "admin_comments", sortable: false, width: "150px" },
                { text: "Invoice Status", value: "invoice_status", sortable: false, width: "150px" },
                { text: "A/R Status", value: "ar_status", sortable: false, width: "150px" },
                { text: "Bank Details", value: "bank_details", sortable: false, width: "150px" },
                { text: "Client Payment Status", value: "client_payment_status", sortable: false, width: "180px" },
                { text: "Time Doctor Status", value: "time_doctor_status", sortable: false, width: "180px" },
                { text: "Date Paid", value: "payment_receipt_uploaded_at", sortable: false, width: "100px" },
                { text: "Status", value: "status", sortable: false, width: "100px" },
                { text: "Approved Amount", value: "approved_amount", sortable: false, align: "right" },
                { text: "Dollar Rate", value: "dollar_rate_display", sortable: false, align: "right" },
                { text: "PHP Rate", value: "php_rate", sortable: false, align: "right" },
                { text: "Transfer Fee", value: "transfer_fee_display", sortable: false, align: "right" },
                { text: "Total Salary Expense", value: "total_salary_expense", sortable: false, align: "right" },
            ],
            vaHeaders: [
                { text: "", value: "avatar", sortable: false, width: "50px" },
                { text: "Name", value: "name" },
                { text: "Email", value: "email" },
                { text: "Hourly Rate", value: "hourly_rate", sortable: false },
                { text: "Currency", value: "payment_currency", sortable: false },
                { text: "Actions", value: "actions", sortable: false, width: "80px" },
            ],
            // USD Expense Tab
            dollarRate: 0,
            transferFee: 0,
            editingTransferFee: 0,
            editingDollarRate: 0,
            isSavingRate: false,
            // Server-side totals (populated from API meta)
            serverTotals: { total_usd_approved: 0, total_php_rate: 0, total_transfer_fees: 0, total_salary_expense: 0 },
            serverPTTotals: { total_usd_approved: 0, total_php_rate: 0, total_transfer_fees: 0, total_salary_expense: 0 },
            usdDateRangeMenu: false,
            usdDateRange: [],
            usdExpenseHeaders: [
                { text: "Timestamp", value: "submitted_at", width: "120px" },
                { text: "Employee", value: "user", width: "180px" },
                { text: "Invoice Service Period", value: "invoice_period", width: "140px" },
                { text: "Company", value: "company", width: "120px" },
                { text: "Invoice #", value: "invoice_number" },
                { text: "Hours Worked", value: "hours_worked", align: "right" },
                { text: "Invoice Amount", value: "invoice_amount", align: "right" },
                { text: "Invoice (PDF)", value: "invoice_file_url", width: "100px" },
                { text: "Comments", value: "comments", width: "150px" },
                { text: "COR (BIR 2303)", value: "cor_file_url", width: "100px" },
                { text: "Ack Receipt", value: "acknowledgement_receipt_url", width: "100px" },
                { text: "Tax Returns", value: "tax_return_urls", width: "100px" },
                { text: "Date Paid", value: "payment_receipt_uploaded_at", width: "100px" },
                { text: "Dollar Rate (CF)", value: "dollar_rate_display", align: "right" },
                { text: "PHP Rate (CF)", value: "php_rate", align: "right" },
                { text: "Transfer Fee", value: "transfer_fee_display", align: "right" },
                { text: "Total Salary Expense", value: "total_salary_expense", align: "right" },
            ],
            // Dialogs
            approveDialog: false,
            denyDialog: false,
            receiptDialog: false,
            detailsDialog: false,
            timeBreakdownDialog: false,
            timeBreakdownLoading: false,
            timeBreakdownError: null,
            timeBreakdown: null,
            timeBreakdownSubmission: null,
            selectedSubmission: null,
            approvedAmount: null,
            denyReason: "",
            receiptFile: null,
            // Date paid menus state
            datePaidMenus: {},
            // Delete confirmation
            deleteDialog: false,
            submissionToDelete: null,
            // Trashed/deleted submissions
            showDeleted: false,
            // Restore confirmation
            restoreDialog: false,
            submissionToRestore: null,
            // Unapprove confirmation
            unapproveDialog: false,
            submissionToUnapprove: null,
            // Currency change
            currencyDialog: false,
            currencySubmission: null,
            currencySubmissionType: null, // 'normal' or 'pt'
            newCurrency: null,
            newInvoiceAmount: null,
            // PT-specific state
            ptStatusFilter: "all",
            ptUserFilter: null,
            ptCompanyFilter: null,
            ptCurrencyFilter: "All",
            ptQuarterFilter: "all",
            ptDateRangeMenu: false,
            ptDateRange: [],
            ptPaidDateRangeMenu: false,
            ptPaidDateRange: [],
            ptShowDeleted: false,
            // PT status field filters
            ptClientNameFilter: [],
            ptClientNameOptions: [],
            ptInvoiceStatusFilter: null,
            ptArStatusFilter: null,
            ptBankDetailsFilter: null,
            ptClientPaymentStatusFilter: null,
            ptTimeDoctorStatusFilter: null,
            editingPTTransferFee: 0,
            editingPTDollarRate: 0,
            ptDatePaidMenus: {},
            // PT Dialogs
            ptApproveDialog: false,
            ptDenyDialog: false,
            ptDeleteDialog: false,
            ptRestoreDialog: false,
            ptUnapproveDialog: false,
            selectedPTSubmission: null,
            ptApprovedAmount: null,
            ptDenyReason: "",
            ptSubmissionToDelete: null,
            ptSubmissionToRestore: null,
            ptSubmissionToUnapprove: null,
            // PT Additional Document
            ptAdditionalDocDialog: false,
            ptAdditionalDocSubmission: null,
            ptAdditionalDocFile: null,
            additionalDocIsPT: true,
            // Invoice PDF upload (normal + PT)
            invoiceUploadDialog: false,
            invoiceUploadSubmission: null,
            invoiceUploadFile: null,
            invoiceUploadIsPT: false,
            // Exceptions filters
            exceptionResolvedFilter: 'unresolved',
            exceptionSourceFilter: 'all',
            exceptionUserFilter: null,
            exceptionTypeFilter: null,
            exceptionNotesExpanded: {},
            dialogExceptionNotesExpanded: {},
            exceptionResolvedOptions: [
                { text: 'Unresolved', value: 'unresolved' },
                { text: 'Resolved', value: 'resolved' },
                { text: 'All', value: 'all' },
            ],
            exceptionSourceOptions: [
                { text: 'All Sources', value: 'all' },
                { text: 'Regular Submission', value: 'regular' },
                { text: 'PT Submission', value: 'pt' },
            ],
            // Exceptions dialog
            exceptionsDialog: false,
            exceptionsSubmission: null,
        };
    },
    computed: {
        breakdownDiffers() {
            const b = this.timeBreakdown;
            if (!b || b.submitted_hours === null) return false;
            return Math.abs(b.total_seconds / 3600 - b.submitted_hours) >= 0.02;
        },
        canDeleteSubmissions() {
            const allowedEmails = ['daniza@stagit.ie', 'dillongunn@gmail.com', 'accounts@profitabletradesmen.com'];
            return this.user && allowedEmails.includes(this.user.email?.toLowerCase());
        },
        // ----- helper: compute expense fields for any submissions array -----
        _computeExpense() {
            return (submissions) => submissions.map((submission) => {
                const isNotDeniedOrDeleted = submission.status !== 'denied' && !submission.deleted_at;
                const isUsd = submission.currency === 'USD';
                const isPhp = submission.currency === 'PHP';
                const approvedAmount = parseFloat(submission.approved_amount) || 0;
                const submissionTransferFee = submission.transfer_fee !== null && submission.transfer_fee !== undefined
                    ? parseFloat(submission.transfer_fee)
                    : this.transferFee;
                const submissionDollarRate = submission.dollar_rate !== null && submission.dollar_rate !== undefined
                    ? parseFloat(submission.dollar_rate)
                    : this.dollarRate;
                let phpRate = 0;
                let totalSalaryExpense = 0;
                if (isNotDeniedOrDeleted) {
                    if (isUsd) {
                        phpRate = approvedAmount * submissionDollarRate;
                        totalSalaryExpense = phpRate + submissionTransferFee;
                    } else if (isPhp) {
                        phpRate = approvedAmount;
                        totalSalaryExpense = approvedAmount + submissionTransferFee;
                    }
                }
                return { ...submission, php_rate: phpRate, submission_transfer_fee: submissionTransferFee, submission_dollar_rate: submissionDollarRate, total_salary_expense: totalSalaryExpense };
            });
        },
        paginatedSubmissionsWithExpense() {
            return this._computeExpense(this.paginatedSubmissions);
        },
        paginatedPTSubmissionsWithExpense() {
            return this._computeExpense(this.paginatedPTSubmissions);
        },
        // ----- quarter options: last 12 quarters -----
        quarterOptions() {
            return this._generateQuarterOptions();
        },
        ptQuarterOptions() {
            return this._generateQuarterOptions();
        },
        // ----- expense totals (all filtered data — server-side) -----
        salaryExpenseSubmissions() {
            return this.paginatedSubmissionsWithExpense.filter((s) =>
                (s.currency === "USD" || s.currency === "PHP") && s.status !== "denied" && !s.deleted_at
            );
        },
        usdSubmissionsWithExpense() {
            return this.paginatedSubmissionsWithExpense.filter((s) => s.currency === "USD" && s.status !== "denied" && !s.deleted_at);
        },
        totalUsdApproved() {
            return this.serverTotals.total_usd_approved || 0;
        },
        totalPhpRate() {
            return this.serverTotals.total_php_rate || 0;
        },
        totalTransferFees() {
            return this.serverTotals.total_transfer_fees || 0;
        },
        totalSalaryExpense() {
            return this.serverTotals.total_salary_expense || 0;
        },
        ptSalaryExpenseSubmissions() {
            return this.paginatedPTSubmissionsWithExpense.filter((s) =>
                (s.currency === "USD" || s.currency === "PHP") && s.status !== "denied" && !s.deleted_at
            );
        },
        ptUsdSubmissionsWithExpense() {
            return this.paginatedPTSubmissionsWithExpense.filter((s) => s.currency === "USD" && s.status !== "denied" && !s.deleted_at);
        },
        totalPTUsdApproved() {
            return this.serverPTTotals.total_usd_approved || 0;
        },
        totalPTPhpRate() {
            return this.serverPTTotals.total_php_rate || 0;
        },
        totalPTTransferFees() {
            return this.serverPTTotals.total_transfer_fees || 0;
        },
        totalPTSalaryExpense() {
            return this.serverPTTotals.total_salary_expense || 0;
        },
        // ----- formatting -----
        formattedDateRange() {
            if (!this.dateRange || this.dateRange.length === 0) return "";
            if (this.dateRange.length === 1) return this.$date(this.dateRange[0]).format('MMM D, YYYY');
            const sorted = [...this.dateRange].sort();
            return `${this.$date(sorted[0]).format('MMM D, YYYY')} - ${this.$date(sorted[1]).format('MMM D, YYYY')}`;
        },
        formattedPaidDateRange() {
            if (!this.paidDateRange || this.paidDateRange.length === 0) return "";
            if (this.paidDateRange.length === 1) return this.$date(this.paidDateRange[0]).format('MMM D, YYYY');
            const sorted = [...this.paidDateRange].sort();
            return `${this.$date(sorted[0]).format('MMM D, YYYY')} - ${this.$date(sorted[1]).format('MMM D, YYYY')}`;
        },
        formattedPTDateRange() {
            if (!this.ptDateRange || this.ptDateRange.length === 0) return "";
            if (this.ptDateRange.length === 1) return this.$date(this.ptDateRange[0]).format('MMM D, YYYY');
            const sorted = [...this.ptDateRange].sort();
            return `${this.$date(sorted[0]).format('MMM D, YYYY')} - ${this.$date(sorted[1]).format('MMM D, YYYY')}`;
        },
        formattedPTPaidDateRange() {
            if (!this.ptPaidDateRange || this.ptPaidDateRange.length === 0) return "";
            if (this.ptPaidDateRange.length === 1) return this.$date(this.ptPaidDateRange[0]).format('MMM D, YYYY');
            const sorted = [...this.ptPaidDateRange].sort();
            return `${this.$date(sorted[0]).format('MMM D, YYYY')} - ${this.$date(sorted[1]).format('MMM D, YYYY')}`;
        },
        usersWithRates() {
            return this.vaProfiles.map((user) => ({
                ...user,
                hourly_rate: user.hourly_rate || 0,
                payment_currency: user.payment_currency || "USD",
            }));
        },
        // ----- exceptions -----
        filteredExceptions() {
            let filtered = this.exceptions;
            if (this.exceptionResolvedFilter === 'unresolved') {
                filtered = filtered.filter(e => !e.is_resolved);
            } else if (this.exceptionResolvedFilter === 'resolved') {
                filtered = filtered.filter(e => e.is_resolved);
            }
            if (this.exceptionSourceFilter === 'regular') {
                filtered = filtered.filter(e => e.payroll_submission_id && !e.pt_submission_id);
            } else if (this.exceptionSourceFilter === 'pt') {
                filtered = filtered.filter(e => e.pt_submission_id);
            }
            if (this.exceptionUserFilter) {
                filtered = filtered.filter(e => e.user_id === this.exceptionUserFilter);
            }
            if (this.exceptionTypeFilter) {
                filtered = filtered.filter(e => e.exception_type === this.exceptionTypeFilter);
            }
            return filtered;
        },
        exceptionUserOptions() {
            const usersMap = new Map();
            this.exceptions.forEach(e => {
                if (e.user && !usersMap.has(e.user_id)) {
                    usersMap.set(e.user_id, { text: e.user.name, value: e.user_id });
                }
            });
            return Array.from(usersMap.values()).sort((a, b) => a.text.localeCompare(b.text));
        },
        exceptionTypeOptions() {
            const types = new Set();
            this.exceptions.forEach(e => { if (e.exception_type) types.add(e.exception_type); });
            return Array.from(types).sort().map(t => ({ text: this.formatExceptionType(t), value: t }));
        },
        unresolvedExceptionCount() {
            return this.exceptions.filter(e => !e.is_resolved).length;
        },
        // ----- legacy compat (needed by old export methods until replaced) -----
        filteredSubmissions() {
            return this.paginatedSubmissions;
        },
        filteredPTSubmissions() {
            return this.paginatedPTSubmissions;
        },
        filteredSubmissionsWithExpense() {
            return this.paginatedSubmissionsWithExpense;
        },
        filteredPTSubmissionsWithExpense() {
            return this.paginatedPTSubmissionsWithExpense;
        },
        // ----- keep these so remaining old computed refs don't break -----
        uniqueUsers() { return this.users; },
        uniqueCompanies() { return this.companies; },
        uniquePTUsers() { return this.users; },
        uniquePTCompanies() { return this.companies; },
        filteredExportSubmissions() {
            return this.paginatedSubmissionsWithExpense;
        },
        filteredExportPTSubmissions() {
            return this.paginatedPTSubmissionsWithExpense;
        },
    },
    watch: {
        payrollRate: {
            handler(newRate) {
                if (newRate) {
                    this.dollarRate = parseFloat(newRate.dollar_rate) || 0;
                    this.transferFee = parseFloat(newRate.transfer_fee) || 0;
                }
            },
            immediate: true,
            deep: true,
        },
        dollarRate(newVal, oldVal) {
            // Auto-save on change (debounced). Only a user edit saves: copying in the
            // saved rate on page load (0 -> server value) must not re-save and toast.
            if (oldVal !== undefined && newVal !== oldVal && !this.isLoadingRate && !this.matchesSavedRate('dollar_rate', newVal)) {
                this.debouncedSavePayrollRate();
                // Re-fetch totals since they depend on the global dollar rate
                this.fetchSubmissionsPage();
                this.fetchPTSubmissionsPage();
            }
        },
        transferFee(newVal, oldVal) {
            // Auto-save on change (debounced); see dollarRate for why the saved value is skipped
            if (oldVal !== undefined && newVal !== oldVal && !this.isLoadingRate && !this.matchesSavedRate('transfer_fee', newVal)) {
                this.debouncedSavePayrollRate();
                // Re-fetch totals since they depend on the global transfer fee
                this.fetchSubmissionsPage();
                this.fetchPTSubmissionsPage();
            }
        },
        submissionsSortBy() {
            this.submissionsOptions = { ...this.submissionsOptions, page: 1 };
            this.fetchSubmissionsPage();
        },
        submissionsSortDesc() {
            this.submissionsOptions = { ...this.submissionsOptions, page: 1 };
            this.fetchSubmissionsPage();
        },
        submissionsOptions: {
            handler(opts, old) {
                if (old && (opts.page !== old.page || opts.itemsPerPage !== old.itemsPerPage)) {
                    this.fetchSubmissionsPage();
                }
            },
            deep: true,
        },
        ptSubmissionsSortBy() {
            this.ptSubmissionsOptions = { ...this.ptSubmissionsOptions, page: 1 };
            this.fetchPTSubmissionsPage();
        },
        ptSubmissionsSortDesc() {
            this.ptSubmissionsOptions = { ...this.ptSubmissionsOptions, page: 1 };
            this.fetchPTSubmissionsPage();
        },
        ptSubmissionsOptions: {
            handler(opts, old) {
                if (old && (opts.page !== old.page || opts.itemsPerPage !== old.itemsPerPage)) {
                    this.fetchPTSubmissionsPage();
                }
            },
            deep: true,
        },
        submissionsRefreshKey() {
            this.fetchSubmissionsPage();
        },
        ptSubmissionsRefreshKey() {
            this.fetchPTSubmissionsPage();
        },
        dateRange: {
            handler() { this.applySubmissionsFilters(); },
            deep: true,
        },
        paidDateRange: {
            handler() { this.applySubmissionsFilters(); },
            deep: true,
        },
        userFilter() { this.applySubmissionsFilters(); },
        companyFilter() { this.applySubmissionsFilters(); },
        currencyFilter() { this.applySubmissionsFilters(); },
        quarterFilter() { this.applySubmissionsFilters(); },
        statusFilter() { this.applySubmissionsFilters(); },
        ptDateRange: {
            handler() { this.applyPTSubmissionsFilters(); },
            deep: true,
        },
        ptPaidDateRange: {
            handler() { this.applyPTSubmissionsFilters(); },
            deep: true,
        },
        ptUserFilter() { this.applyPTSubmissionsFilters(); },
        ptCompanyFilter() { this.applyPTSubmissionsFilters(); },
        ptCurrencyFilter() { this.applyPTSubmissionsFilters(); },
        ptQuarterFilter() { this.applyPTSubmissionsFilters(); },
        ptStatusFilter() { this.applyPTSubmissionsFilters(); },
        ptClientNameFilter() { this.applyPTSubmissionsFilters(); },
        ptInvoiceStatusFilter() { this.applyPTSubmissionsFilters(); },
        ptArStatusFilter() { this.applyPTSubmissionsFilters(); },
        ptBankDetailsFilter() { this.applyPTSubmissionsFilters(); },
        ptClientPaymentStatusFilter() { this.applyPTSubmissionsFilters(); },
        ptTimeDoctorStatusFilter() { this.applyPTSubmissionsFilters(); },
        lastUpdatedSubmission(val) {
            if (!val) return;
            const idx = this.paginatedSubmissions.findIndex(s => s.id === val.id);
            if (idx !== -1) this.$set(this.paginatedSubmissions, idx, val);
        },
        lastUpdatedPTSubmission(val) {
            if (!val) return;
            const idx = this.paginatedPTSubmissions.findIndex(s => s.id === val.id);
            if (idx !== -1) this.$set(this.paginatedPTSubmissions, idx, val);
        },
    },
    mounted() {
        this.fetchSubmissionsPage();
        this.fetchPTSubmissionsPage();
    },
    methods: {
        async openTimeBreakdown(item, type) {
            this.timeBreakdownSubmission = item;
            this.timeBreakdown = null;
            this.timeBreakdownError = null;
            this.timeBreakdownLoading = true;
            this.timeBreakdownDialog = true;
            try {
                const response = await this.$axios.get(`/api/payroll/manager/submissions/${item.id}/time-breakdown`, { params: { type } });
                if (response.data.response) {
                    this.timeBreakdown = response.data.data;
                } else {
                    this.timeBreakdownError = response.data.message || 'Failed to load the time breakdown';
                }
            } catch (error) {
                this.timeBreakdownError = error.response?.data?.message || error.message || 'Failed to load the time breakdown';
            } finally {
                this.timeBreakdownLoading = false;
            }
        },
        breakdownSourceLabel(source) {
            return { classic: 'Classic', td2: 'TD2', dgtasks: 'DGTasks' }[source] || source;
        },
        breakdownSourceColor(source) {
            return { td2: 'green lighten-4', dgtasks: 'blue lighten-4' }[source] || 'grey lighten-3';
        },
        breakdownPercent(seconds) {
            const total = this.timeBreakdown?.total_seconds || 0;
            return total ? Math.round((seconds / total) * 100) : 0;
        },
        // Get quarter for a date { quarter: 1-4, year: YYYY }
        getQuarterForDate(dateStr) {
            const d = new Date(dateStr);
            const month = d.getMonth();
            const year = d.getFullYear();
            const quarter = Math.floor(month / 3) + 1;
            return { quarter, year };
        },
        // Get tax return due date for a quarter
        // Q1: May 15, Q2: Aug 15, Q3: Nov 15, Q4: April 15 next year
        getTaxReturnDueDate(quarter, year) {
            const dueDates = {
                1: new Date(year, 4, 15),
                2: new Date(year, 7, 15),
                3: new Date(year, 10, 15),
                4: new Date(year + 1, 3, 15),
            };
            return dueDates[quarter];
        },
        // Check if user has COR uploaded in a submission created BEFORE the given date
        userHasPriorCor(userId, createdAt) {
            if (!userId || !createdAt) return false;
            const currentDate = new Date(createdAt);
            return this.paginatedSubmissions.some(s => 
                s.user?.id === userId && 
                s.cor_file_path && 
                new Date(s.created_at) < currentDate
            );
        },
        // Check if user has tax returns in the SAME quarter as the submission
        userHasTaxReturnsForQuarter(userId, createdAt) {
            if (!userId || !createdAt) return { hasTaxReturns: false, isPastDue: false };
            const submissionQuarter = this.getQuarterForDate(createdAt);
            const dueDate = this.getTaxReturnDueDate(submissionQuarter.quarter, submissionQuarter.year);
            const now = new Date();
            const isPastDue = now > dueDate;
            
            const hasTaxReturns = this.paginatedSubmissions.some(s => 
                s.user?.id === userId && 
                s.tax_return_urls && 
                s.tax_return_urls.length > 0 && 
                (() => {
                    const sq = this.getQuarterForDate(s.created_at);
                    return sq.quarter === submissionQuarter.quarter && sq.year === submissionQuarter.year;
                })()
            );
            
            return { hasTaxReturns, isPastDue };
        },
        ptUserHasPriorCor(userId, createdAt) {
            if (!userId || !createdAt) return false;
            const currentDate = new Date(createdAt);
            return this.paginatedPTSubmissions.some(s => 
                s.user?.id === userId && 
                s.cor_file_path && 
                new Date(s.created_at) < currentDate
            );
        },
        ptUserHasTaxReturnsForQuarter(userId, createdAt) {
            if (!userId || !createdAt) return { hasTaxReturns: false, isPastDue: false };
            const submissionQuarter = this.getQuarterForDate(createdAt);
            const dueDate = this.getTaxReturnDueDate(submissionQuarter.quarter, submissionQuarter.year);
            const now = new Date();
            const isPastDue = now > dueDate;
            
            const hasTaxReturns = this.paginatedPTSubmissions.some(s => 
                s.user?.id === userId && 
                s.tax_return_urls && 
                s.tax_return_urls.length > 0 && 
                (() => {
                    const sq = this.getQuarterForDate(s.created_at);
                    return sq.quarter === submissionQuarter.quarter && sq.year === submissionQuarter.year;
                })()
            );
            
            return { hasTaxReturns, isPastDue };
        },
        confirmDeleteSubmission(submission) {
            this.submissionToDelete = submission;
            this.deleteDialog = true;
        },
        cancelDelete() {
            this.deleteDialog = false;
            this.submissionToDelete = null;
        },
        deleteSubmission() {
            if (this.submissionToDelete) {
                this.$emit('delete-submission', this.submissionToDelete);
                this.deleteDialog = false;
                this.submissionToDelete = null;
            }
        },
        // Toggle showing deleted submissions
        toggleShowDeleted() {
            this.showDeleted = !this.showDeleted;
            this.submissionsOptions = { ...this.submissionsOptions, page: 1 };
            this.fetchSubmissionsPage();
        },
        // Restore submission methods
        confirmRestoreSubmission(submission) {
            this.submissionToRestore = submission;
            this.restoreDialog = true;
        },
        cancelRestore() {
            this.restoreDialog = false;
            this.submissionToRestore = null;
        },
        restoreSubmission() {
            if (this.submissionToRestore) {
                this.$emit('restore-submission', this.submissionToRestore);
                this.restoreDialog = false;
                this.submissionToRestore = null;
            }
        },
        // Unapprove submission methods
        confirmUnapprove(submission) {
            this.submissionToUnapprove = submission;
            this.unapproveDialog = true;
        },
        cancelUnapprove() {
            this.unapproveDialog = false;
            this.submissionToUnapprove = null;
        },
        unapproveSubmission() {
            if (this.submissionToUnapprove) {
                this.$emit('unapprove-submission', this.submissionToUnapprove);
                this.unapproveDialog = false;
                this.submissionToUnapprove = null;
            }
        },
        getInitials(name) {
            if (!name) return "?";
            return name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
        },
        getStatusColor(status) {
            const colors = {
                pending: "orange",
                approved: "success",
                denied: "error",
            };
            return colors[status] || "grey";
        },
        getStatusFieldColor(field, value) {
            const colorMap = {
                invoice_status: {
                    'YES (PT Accounts)': '#4caf50',
                    'Validated for Approval (Leah)': '#e0e0e0',
                    'ON HOLD': '#ff9800',
                    'YES (Daniza)': '#fdd835',
                    'VERIFYING': '#9c27b0',
                    'NO Bank Details': '#424242',
                    'NO COR!': '#f44336',
                    'NO A/R': '#f44336',
                    'WITH COR': '#ffccbc',
                    'WITH A/R': '#ffccbc',
                },
                ar_status: {
                    'YES (PT Accounts)': '#4caf50',
                    'Validated for Approval (Leah)': '#e0e0e0',
                    'ON HOLD': '#ff9800',
                    'YES (Daniza)': '#fdd835',
                    'VERIFYING': '#9c27b0',
                    'NO Bank Details': '#424242',
                    'NO COR!': '#f44336',
                    'NO A/R': '#f44336',
                    'WITH COR': '#ffccbc',
                    'WITH A/R': '#ffccbc',
                },
                bank_details: {
                    'Same': '#e0e0e0',
                    'NEW': '#5d4037',
                    'NONE': '#ff9800',
                    'ERROR': '#f44336',
                },
                client_payment_status: {
                    'NO PAYMENT YET': '#f44336',
                    'CLIENT PAID': '#4caf50',
                    'PT Expense': '#424242',
                    'DG Ventures Expense': '#9e9e9e',
                    'PT Expense (Training)': '#5d4037',
                    'CLIENT NOT GOING TO PAY': '#212121',
                    'Revolution Doors/Windows': '#e0e0e0',
                    'Go Irish Tours': '#e0e0e0',
                    'Funeral Director Solutions': '#f44336',
                },
                time_doctor_status: {
                    'Matches Invoice': '#4caf50',
                    'For Clarifications': '#9c27b0',
                    'TIME DOCTOR VERIFIED': '#e0e0e0',
                    'NO TD record - needs approval from Manager': '#f44336',
                    'NO NEED TD (as per client)': '#e0e0e0',
                },
            };
            return (colorMap[field] && colorMap[field][value]) || '#e0e0e0';
        },
        getStatusFieldTextColor(field, value) {
            const darkBg = ['#424242', '#5d4037', '#212121', '#9c27b0', '#f44336', '#4caf50', '#ff9800'];
            const color = this.getStatusFieldColor(field, value);
            return darkBg.includes(color) ? 'white' : '#333';
        },
        getExceptionTypeColor(type) {
            const colors = {
                hours_mismatch: "warning",
                rate_discrepancy: "error",
                missing_documents: "info",
            };
            return colors[type] || "grey";
        },
        formatExceptionType(type) {
            const types = {
                hours_mismatch: "Hours Mismatch",
                rate_discrepancy: "Rate Discrepancy",
                missing_documents: "Missing Documents",
            };
            return types[type] || type;
        },
        toggleExceptionResolved(exception, isResolved) {
            if (isResolved) {
                this.$emit('resolve-exception', exception.id, exception.resolution_notes || null);
            } else {
                this.$emit('unresolve-exception', exception.id);
            }
        },
        updateExceptionNotes(exception, notes) {
            this.$emit('update-exception-notes', exception.id, notes);
        },
        customSort(items, sortBy, sortDesc) {
            if (!sortBy || sortBy.length === 0) return items;
            const key = sortBy[0];
            const desc = sortDesc[0];
            return [...items].sort((a, b) => {
                let aVal, bVal;
                if (key === 'user') {
                    aVal = (a.user?.first_name && a.user?.last_name) ? (a.user.first_name + ' ' + a.user.last_name) : (a.user?.name || '');
                    bVal = (b.user?.first_name && b.user?.last_name) ? (b.user.first_name + ' ' + b.user.last_name) : (b.user?.name || '');
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                } else {
                    aVal = a[key];
                    bVal = b[key];
                }
                if (aVal === null || aVal === undefined) aVal = '';
                if (bVal === null || bVal === undefined) bVal = '';
                let result;
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    result = aVal.localeCompare(bVal);
                } else {
                    result = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                }
                return desc ? -result : result;
            });
        },
        formatDateRange(fromDate, toDate) {
            if (!fromDate || !toDate) return "-";
            const from = this.$date(fromDate).format("MMM D");
            const to = this.$date(toDate).format("MMM D, YYYY");
            return `${from} - ${to}`;
        },
        formatDateTime(dateStr) {
            if (!dateStr) return "-";
            return this.$date(dateStr).format("MMM D, YYYY h:mm A");
        },
        formatHours(decimal) {
            if (!decimal && decimal !== 0) return '0h';
            const num = parseFloat(decimal);
            const hours = Math.floor(num);
            const minutes = Math.round((num - hours) * 60);
            if (minutes === 0) return `${hours}h`;
            return `${hours}h ${minutes}m`;
        },
        formatCurrency(amount) {
            if (amount === null || amount === undefined) return "-";
            return amount.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        formatExceptionDescription(description) {
            if (!description) return "";
            // Round any decimal numbers with many decimal places to 2 decimal places
            // Pattern matches numbers like 80.166666666667h and rounds them
            return description.replace(/(\d+\.\d{3,})h/g, (match, number) => {
                const rounded = parseFloat(number).toFixed(2);
                return `${rounded}h`;
            });
        },
        // Approve
        openExceptionsDialog(submission) {
            this.exceptionsSubmission = submission;
            this.exceptionsDialog = true;
        },
        // Currency Change
        openCurrencyDialog(submission, type) {
            this.currencySubmission = submission;
            this.currencySubmissionType = type;
            this.newCurrency = submission.currency || 'USD';
            this.newInvoiceAmount = submission.invoice_amount;
            this.currencyDialog = true;
        },
        closeCurrencyDialog() {
            this.currencyDialog = false;
            this.currencySubmission = null;
            this.currencySubmissionType = null;
            this.newCurrency = null;
            this.newInvoiceAmount = null;
        },
        confirmCurrencyChange() {
            const event = this.currencySubmissionType === 'pt' ? 'pt-update-currency' : 'update-currency';
            this.$emit(event, this.currencySubmission, this.newCurrency, this.newInvoiceAmount);
            this.closeCurrencyDialog();
        },
        // Approve
        openApproveDialog(submission) {
            this.selectedSubmission = submission;
            this.approvedAmount = submission.invoice_amount;
            this.approveDialog = true;
        },
        closeApproveDialog() {
            this.approveDialog = false;
            this.selectedSubmission = null;
            this.approvedAmount = null;
        },
        confirmApprove() {
            this.$emit("approve", this.selectedSubmission, this.approvedAmount);
            this.closeApproveDialog();
        },
        // Deny
        openDenyDialog(submission) {
            this.selectedSubmission = submission;
            this.denyReason = "";
            this.denyDialog = true;
        },
        closeDenyDialog() {
            this.denyDialog = false;
            this.selectedSubmission = null;
            this.denyReason = "";
        },
        confirmDeny() {
            this.$emit("deny", this.selectedSubmission, this.denyReason);
            this.closeDenyDialog();
        },
        // Receipt
        openReceiptDialog(submission) {
            this.selectedSubmission = submission;
            this.receiptFile = null;
            this.receiptDialog = true;
        },
        closeReceiptDialog() {
            this.receiptDialog = false;
            this.selectedSubmission = null;
            this.receiptFile = null;
        },
        confirmUploadReceipt() {
            this.$emit("uploadReceipt", this.selectedSubmission, this.receiptFile);
            this.closeReceiptDialog();
        },
        // Payment date methods
        updatePaymentDate(submission, date) {
            this.$set(this.datePaidMenus, submission.id, false);
            this.$emit("updatePaymentDate", submission, date);
        },
        setPaymentDateToday(submission) {
            const today = this.$date().format('YYYY-MM-DD');
            this.$emit("updatePaymentDate", submission, today);
        },
        // Transfer fee methods
        saveTransferFee(submission) {
            const newFee = parseFloat(this.editingTransferFee) || 0;
            this.$emit("updateTransferFee", submission.id, newFee);
        },
        // Dollar rate methods
        saveDollarRate(submission) {
            const newRate = parseFloat(this.editingDollarRate) || 0;
            this.$emit("updateDollarRate", submission.id, newRate);
        },
        // Admin comments methods
        updateAdminComments(submission, value) {
            this.$emit("updateAdminComments", submission.id, value);
        },
        // View details
        viewSubmissionDetails(submission) {
            this.selectedSubmission = submission;
            this.detailsDialog = true;
        },
        // User rates
        async loadVAProfiles() {
            try {
                this.isLoadingVAProfiles = true;
                const response = await this.$axios.get('/api/payroll/manager/va-profiles');
                if (response.data.response && response.data.data) {
                    this.vaProfiles = response.data.data;
                }
            } catch (error) {
                console.error('Error loading VA profiles:', error);
            } finally {
                this.isLoadingVAProfiles = false;
            }
        },
        updateUserRate() {
            // This is called on change, we can debounce or wait for save
        },
        saveUserRate(user) {
            this.$emit("updateRate", user, user.hourly_rate, user.payment_currency);
        },
        // Date range filter
        clearDateRange() {
            this.dateRange = [];
        },
        clearPaidDateRange() {
            this.paidDateRange = [];
        },
        clearUsdDateRange() {
            this.usdDateRange = [];
        },
        // Export CSV
        async exportSubmissionsCSV() {
            this.$toast.info('Fetching all submissions for export...');
            let allSubmissions;
            try {
                const params = { ...this._buildSubmissionsParams() };
                delete params.page; delete params.per_page; delete params.sort_by; delete params.sort_dir;
                const response = await this.$axios.get('/api/payroll/manager/submissions', params);
                if (!response.data.response && !response.data.data) {
                    this.$toast.error('Failed to fetch submissions for export');
                    return;
                }
                allSubmissions = response.data.data || [];
            } catch (error) {
                this.$toast.error('Failed to fetch submissions for export');
                return;
            }
            const allWithExpense = this._computeExpense(allSubmissions);
            if (allWithExpense.length === 0) {
                this.$toast.warning('No submissions to export');
                return;
            }

            const headers = [
                'Timestamp',
                'Employee Name',
                'Email',
                'Invoice Service Period',
                'Company',
                'Invoice Number',
                'Hours Worked',
                'Currency',
                'Invoice Amount',
                'Invoice (PDF)',
                'Comments',
                'COR (BIR 2303)',
                'Ack Receipt',
                'Tax Returns',
                'Date Paid',
                'Status',
                'Approved Amount',
                'Dollar Rate',
                'PHP Rate',
                'Transfer Fee',
                'Total Salary Expense'
            ];

            const rows = allWithExpense.map(s => {
                const hasExpense = (s.currency === 'USD' || s.currency === 'PHP') && s.status !== 'denied' && !s.deleted_at;
                const isUsd = s.currency === 'USD';
                return [
                    s.submitted_at ? this.$date(s.submitted_at).format('YYYY-MM-DD HH:mm') : '',
                    s.user?.first_name && s.user?.last_name ? s.user.first_name + ' ' + s.user.last_name : (s.user?.name || ''),
                    s.user?.email || '',
                    `${s.invoice_period_from || ''} - ${s.invoice_period_to || ''}`,
                    s.company?.name || '',
                    s.invoice_number || '',
                    s.hours_worked || '0',
                    s.currency || 'USD',
                    s.invoice_amount || '0',
                    s.invoice_file_path ? `https://api.dgventureslimited.com/storage/${s.invoice_file_path}` : '',
                    s.comments || '',
                    s.cor_file_path ? `https://api.dgventureslimited.com/storage/${s.cor_file_path}` : '',
                    s.acknowledgement_receipt_path ? `https://api.dgventureslimited.com/storage/${s.acknowledgement_receipt_path}` : '',
                    s.tax_return_urls?.map(t => `https://api.dgventureslimited.com/storage/${t.path}`).join('; ') || '',
                    s.payment_receipt_uploaded_at ? this.$date(s.payment_receipt_uploaded_at).format('YYYY-MM-DD') : '',
                    s.status || '',
                    s.approved_amount || '',
                    hasExpense && isUsd ? s.submission_dollar_rate : '',
                    hasExpense ? s.php_rate?.toFixed(2) : '',
                    hasExpense ? s.submission_transfer_fee?.toFixed(2) : '',
                    hasExpense ? s.total_salary_expense?.toFixed(2) : ''
                ];
            });

            // Add totals row
            // if (this.salaryExpenseSubmissions.length > 0) {
            //     rows.push([
            //         'TOTALS',
            //         '', '', '', '', '', '', '', '', '', '', '', '', '', '', '',
            //         this.totalUsdApproved.toFixed(2),
            //         '',
            //         this.totalPhpRate.toFixed(2),
            //         this.totalTransferFees.toFixed(2),
            //         this.totalSalaryExpense.toFixed(2)
            //     ]);
            // }

            // Escape CSV values
            const escapeCSV = (val) => {
                if (val === null || val === undefined) return '';
                const stringVal = String(val);
                if (stringVal.includes(',') || stringVal.includes('"') || stringVal.includes('\n')) {
                    return `"${stringVal.replace(/"/g, '""')}"`;
                }
                return stringVal;
            };

            const csvContent = [
                headers.map(escapeCSV).join(','),
                ...rows.map(row => row.map(escapeCSV).join(','))
            ].join('\n');

            // Download CSV
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const filename = `payroll_submissions_${this.$date().format('YYYY-MM-DD_HHmmss')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.$toast.success(`Exported ${allWithExpense.length} submissions`);
        },
        // Export PT Submissions CSV
        async exportPTSubmissionsCSV() {
            this.$toast.info('Fetching all PT submissions for export...');
            let allPTSubmissions;
            try {
                const params = { ...this._buildPTSubmissionsParams() };
                delete params.page; delete params.per_page; delete params.sort_by; delete params.sort_dir;
                const response = await this.$axios.get('/api/payroll/manager/pt/submissions', params);
                if (!response.data.response && !response.data.data) {
                    this.$toast.error('Failed to fetch PT submissions for export');
                    return;
                }
                allPTSubmissions = response.data.data || [];
            } catch (error) {
                this.$toast.error('Failed to fetch PT submissions for export');
                return;
            }
            const allPTWithExpense = this._computeExpense(allPTSubmissions);
            if (allPTWithExpense.length === 0) {
                this.$toast.warning('No PT submissions to export');
                return;
            }

            const headers = [
                'Timestamp',
                'Employee Name',
                'Email',
                'Client Name',
                'Invoice Service Period',
                'Company',
                'Invoice Number',
                'Hours Worked',
                'Currency',
                'Invoice Amount',
                'Invoice (PDF)',
                'Capacity Form',
                'Overtime Approval',
                'Employee DB Form',
                'Signature/NDA',
                'Ack Receipt',
                'Comments',
                'Date Paid',
                'Status',
                'Approved Amount',
                'Dollar Rate',
                'PHP Rate',
                'Transfer Fee',
                'Total Salary Expense'
            ];

            const rows = allPTWithExpense.map(s => {
                const hasExpense = s.status !== 'denied' && !s.deleted_at;
                return [
                    s.submitted_at ? this.$date(s.submitted_at).format('YYYY-MM-DD HH:mm') : '',
                    s.user?.first_name && s.user?.last_name ? s.user.first_name + ' ' + s.user.last_name : (s.user?.name || ''),
                    s.user?.email || '',
                    s.client_name || '',
                    `${s.invoice_period_from || ''} - ${s.invoice_period_to || ''}`,
                    s.company?.name || '',
                    s.invoice_number || '',
                    s.hours_worked || '0',
                    s.currency || 'USD',
                    s.invoice_amount || '0',
                    s.invoice_file_path ? `https://api.dgventureslimited.com/storage/${s.invoice_file_path}` : '',
                    s.capacity_utilization_form_path ? `https://api.dgventureslimited.com/storage/${s.capacity_utilization_form_path}` : '',
                    s.overtime_approval_path ? `https://api.dgventureslimited.com/storage/${s.overtime_approval_path}` : '',
                    s.employee_database_form_path ? `https://api.dgventureslimited.com/storage/${s.employee_database_form_path}` : '',
                    s.signature_acknowledgement_path ? `https://api.dgventureslimited.com/storage/${s.signature_acknowledgement_path}` : '',
                    s.acknowledgement_receipt_path ? `https://api.dgventureslimited.com/storage/${s.acknowledgement_receipt_path}` : '',
                    s.comments || '',
                    s.payment_receipt_uploaded_at ? this.$date(s.payment_receipt_uploaded_at).format('YYYY-MM-DD') : '',
                    s.status || '',
                    s.approved_amount || '',
                    hasExpense ? s.submission_dollar_rate : '',
                    hasExpense ? s.php_rate?.toFixed(2) : '',
                    hasExpense ? s.submission_transfer_fee?.toFixed(2) : '',
                    hasExpense ? s.total_salary_expense?.toFixed(2) : ''
                ];
            });

            const escapeCSV = (val) => {
                if (val === null || val === undefined) return '';
                const stringVal = String(val);
                if (stringVal.includes(',') || stringVal.includes('"') || stringVal.includes('\n')) {
                    return `"${stringVal.replace(/"/g, '""')}"`;
                }
                return stringVal;
            };

            const csvContent = [
                headers.map(escapeCSV).join(','),
                ...rows.map(row => row.map(escapeCSV).join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const filename = `pt_payroll_submissions_${this.$date().format('YYYY-MM-DD_HHmmss')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.$toast.success(`Exported ${allPTWithExpense.length} PT submissions`);
        },
        // Export Invoices
        openExportInvoicesDialog() {
            if (this.filteredSubmissions.length === 0) {
                this.$toast.warning('No submissions to export invoices from');
                return;
            }
            this.exportInvoicesDialog = true;
        },
        closeExportInvoicesDialog() {
            this.exportInvoicesDialog = false;
        },
        confirmExportInvoices() {
            const submissionsWithInvoices = this.filteredSubmissions.filter(s => s.invoice_file_path);

            if (submissionsWithInvoices.length === 0) {
                this.$toast.warning('No invoice files found in filtered submissions');
                this.closeExportInvoicesDialog();
                return;
            }

            // Download each invoice file
            submissionsWithInvoices.forEach((submission, index) => {
                setTimeout(() => {
                    const link = document.createElement('a');
                    link.href = `https://api.dgventureslimited.com/storage/${submission.invoice_file_path}`;
                    link.download = `${submission.user?.first_name && submission.user?.last_name ? submission.user.first_name + ' ' + submission.user.last_name : (submission.user?.name || 'Unknown')}_${submission.invoice_number}_invoice.pdf`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }, index * 300); // Stagger downloads to avoid browser blocking
            });

            this.$toast.success(`Downloading ${submissionsWithInvoices.length} invoice files`);
            this.closeExportInvoicesDialog();
        },
        // Export PT Invoices
        openExportPTInvoicesDialog() {
            if (this.filteredPTSubmissions.length === 0) {
                this.$toast.warning('No PT submissions to export invoices from');
                return;
            }
            this.exportPTInvoicesDialog = true;
        },
        closeExportPTInvoicesDialog() {
            this.exportPTInvoicesDialog = false;
        },
        confirmExportPTInvoices() {
            const submissionsWithInvoices = this.filteredPTSubmissions.filter(s => s.invoice_file_path);

            if (submissionsWithInvoices.length === 0) {
                this.$toast.warning('No invoice files found in filtered PT submissions');
                this.closeExportPTInvoicesDialog();
                return;
            }

            // Download each invoice file
            submissionsWithInvoices.forEach((submission, index) => {
                setTimeout(() => {
                    const link = document.createElement('a');
                    link.href = `https://api.dgventureslimited.com/storage/${submission.invoice_file_path}`;
                    link.download = `${submission.user?.first_name && submission.user?.last_name ? submission.user.first_name + ' ' + submission.user.last_name : (submission.user?.name || 'Unknown')}_${submission.invoice_number}_invoice.pdf`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }, index * 300); // Stagger downloads to avoid browser blocking
            });

            this.$toast.success(`Downloading ${submissionsWithInvoices.length} PT invoice files`);
            this.closeExportPTInvoicesDialog();
        },
        // Export USD Expense CSV
        exportUsdExpenseCSV() {
            if (this.usdSubmissionsWithExpense.length === 0) {
                this.$toast.warning('No USD submissions to export');
                return;
            }

            const headers = [
                'Timestamp',
                'Email Address',
                'Name',
                'Invoice Service Period',
                'Company',
                'Invoice Number',
                'Hours Worked',
                'Invoice Amount',
                'Invoice (PDF)',
                'Comments',
                'COR (BIR 2303)',
                'Ack Receipt (Actual USD/PHP Rate)',
                'Tax Returns',
                'Date Paid',
                'Dollar Rate (CF)',
                'PHP Rate (CF)',
                'Transfer Fee',
                'Total Salary Expense'
            ];

            const rows = this.usdSubmissionsWithExpense.map(s => [
                s.submitted_at ? this.$date(s.submitted_at).format('YYYY-MM-DD HH:mm') : '',
                s.user?.email || '',
                s.user?.first_name && s.user?.last_name ? s.user.first_name + ' ' + s.user.last_name : (s.user?.name || ''),
                `${s.invoice_period_from || ''} - ${s.invoice_period_to || ''}`,
                s.company?.name || '',
                s.invoice_number || '',
                s.hours_worked || '0',
                s.invoice_amount || '0',
                s.invoice_file_url || '',
                s.comments || '',
                s.cor_file_url || '',
                s.acknowledgement_receipt_url || '',
                s.tax_return_urls?.map(t => t.url).join('; ') || '',
                s.payment_receipt_uploaded_at ? this.$date(s.payment_receipt_uploaded_at).format('YYYY-MM-DD') : '',
                s.submission_dollar_rate,
                s.php_rate?.toFixed(2) || '0',
                s.submission_transfer_fee?.toFixed(2) || '0',
                s.total_salary_expense?.toFixed(2) || '0'
            ]);

            // Add totals row
            rows.push([
                'TOTALS',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                this.totalPhpRate.toFixed(2),
                this.totalTransferFees.toFixed(2),
                this.totalSalaryExpense.toFixed(2)
            ]);

            // Escape CSV values
            const escapeCSV = (val) => {
                if (val === null || val === undefined) return '';
                const stringVal = String(val);
                if (stringVal.includes(',') || stringVal.includes('"') || stringVal.includes('\n')) {
                    return `"${stringVal.replace(/"/g, '""')}"`;
                }
                return stringVal;
            };

            const csvContent = [
                headers.map(escapeCSV).join(','),
                ...rows.map(row => row.map(escapeCSV).join(','))
            ].join('\n');

            // Download CSV
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const filename = `usd_salary_expense_${this.$date().format('YYYY-MM-DD_HHmmss')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.$toast.success(`Exported ${this.usdSubmissionsWithExpense.length} USD submissions`);
        },
        // True when value equals the rate already saved on the server (payrollRate prop)
        matchesSavedRate(field, value) {
            if (!this.payrollRate) return false;
            return (parseFloat(this.payrollRate[field]) || 0) === (parseFloat(value) || 0);
        },
        // Save payroll rate
        savePayrollRate() {
            this.$emit("savePayrollRate", this.dollarRate, this.transferFee);
        },
        // Debounced save for auto-save on change
        debouncedSavePayrollRate() {
            if (this.saveRateTimeout) {
                clearTimeout(this.saveRateTimeout);
            }
            this.saveRateTimeout = setTimeout(() => {
                this.savePayrollRate();
            }, 800);
        },

        /*
        |--------------------------------------------------------------------------
        | PT Payroll Methods
        |--------------------------------------------------------------------------
        */

        // PT Date range filter
        clearPTDateRange() {
            this.ptDateRange = [];
        },
        clearPTPaidDateRange() {
            this.ptPaidDateRange = [];
        },

        // Toggle showing deleted PT submissions
        togglePTShowDeleted() {
            this.ptShowDeleted = !this.ptShowDeleted;
            this.ptSubmissionsOptions = { ...this.ptSubmissionsOptions, page: 1 };
            this.fetchPTSubmissionsPage();
        },

        // PT Payment date methods
        updatePTPaymentDate(submission, date) {
            this.$set(this.ptDatePaidMenus, submission.id, false);
            this.$emit("pt-update-payment-date", submission, date);
        },
        setPTPaymentDateToday(submission) {
            const today = this.$date().format('YYYY-MM-DD');
            this.$emit("pt-update-payment-date", submission, today);
        },

        // PT Transfer fee methods
        savePTTransferFee(submission) {
            const newFee = parseFloat(this.editingPTTransferFee) || 0;
            this.$emit("pt-update-transfer-fee", submission.id, newFee);
        },
        // PT Dollar rate methods
        savePTDollarRate(submission) {
            const newRate = parseFloat(this.editingPTDollarRate) || 0;
            this.$emit("pt-update-dollar-rate", submission.id, newRate);
        },
        // PT Admin comments methods
        updatePTAdminComments(submission, value) {
            this.$emit("pt-update-admin-comments", submission.id, value);
        },

        // PT Status field methods
        updatePTStatusField(submission, field, value) {
            this.$emit("pt-update-status-field", submission.id, field, value);
        },

        // PT Approve
        openPTApproveDialog(submission) {
            this.selectedPTSubmission = submission;
            this.ptApprovedAmount = submission.invoice_amount;
            this.ptApproveDialog = true;
        },
        closePTApproveDialog() {
            this.ptApproveDialog = false;
            this.selectedPTSubmission = null;
            this.ptApprovedAmount = null;
        },
        confirmPTApprove() {
            this.$emit("pt-approve", this.selectedPTSubmission, this.ptApprovedAmount);
            this.closePTApproveDialog();
        },

        // PT Deny
        openPTDenyDialog(submission) {
            this.selectedPTSubmission = submission;
            this.ptDenyReason = "";
            this.ptDenyDialog = true;
        },
        closePTDenyDialog() {
            this.ptDenyDialog = false;
            this.selectedPTSubmission = null;
            this.ptDenyReason = "";
        },
        confirmPTDeny() {
            this.$emit("pt-deny", this.selectedPTSubmission, this.ptDenyReason);
            this.closePTDenyDialog();
        },

        // PT Delete
        confirmPTDeleteSubmission(submission) {
            this.ptSubmissionToDelete = submission;
            this.ptDeleteDialog = true;
        },
        cancelPTDelete() {
            this.ptDeleteDialog = false;
            this.ptSubmissionToDelete = null;
        },
        deletePTSubmission() {
            if (this.ptSubmissionToDelete) {
                this.$emit('pt-delete-submission', this.ptSubmissionToDelete);
                this.ptDeleteDialog = false;
                this.ptSubmissionToDelete = null;
            }
        },

        // PT Restore
        confirmPTRestoreSubmission(submission) {
            this.ptSubmissionToRestore = submission;
            this.ptRestoreDialog = true;
        },
        cancelPTRestore() {
            this.ptRestoreDialog = false;
            this.ptSubmissionToRestore = null;
        },
        restorePTSubmission() {
            if (this.ptSubmissionToRestore) {
                this.$emit('pt-restore-submission', this.ptSubmissionToRestore);
                this.ptRestoreDialog = false;
                this.ptSubmissionToRestore = null;
            }
        },

        // PT Unapprove
        confirmPTUnapprove(submission) {
            this.ptSubmissionToUnapprove = submission;
            this.ptUnapproveDialog = true;
        },
        cancelPTUnapprove() {
            this.ptUnapproveDialog = false;
            this.ptSubmissionToUnapprove = null;
        },
        unapprovePTSubmission() {
            if (this.ptSubmissionToUnapprove) {
                this.$emit('pt-unapprove-submission', this.ptSubmissionToUnapprove);
                this.ptUnapproveDialog = false;
                this.ptSubmissionToUnapprove = null;
            }
        },

        // PT Additional Document
        openPTAdditionalDocDialog(submission) {
            this.additionalDocIsPT = true;
            this.ptAdditionalDocSubmission = submission;
            this.ptAdditionalDocFile = null;
            this.ptAdditionalDocDialog = true;
        },
        closePTAdditionalDocDialog() {
            this.ptAdditionalDocDialog = false;
            this.ptAdditionalDocSubmission = null;
            this.ptAdditionalDocFile = null;
        },
        confirmPTAdditionalDocUpload() {
            if (this.ptAdditionalDocSubmission && this.ptAdditionalDocFile) {
                const event = this.additionalDocIsPT ? 'pt-upload-additional-document' : 'upload-additional-document';
                this.$emit(event, this.ptAdditionalDocSubmission, this.ptAdditionalDocFile);
                this.closePTAdditionalDocDialog();
            }
        },
        confirmDeletePTAdditionalDoc(submission) {
            this.$emit('pt-delete-additional-document', submission);
        },

        // Normal submission Additional Document (shares the PT dialog)
        openAdditionalDocDialog(submission) {
            this.openPTAdditionalDocDialog(submission);
            this.additionalDocIsPT = false;
        },
        confirmDeleteAdditionalDoc(submission) {
            this.$emit('delete-additional-document', submission);
        },

        // Invoice PDF upload (normal + PT)
        openInvoiceUploadDialog(submission, isPT) {
            this.invoiceUploadSubmission = submission;
            this.invoiceUploadIsPT = isPT;
            this.invoiceUploadFile = null;
            this.invoiceUploadDialog = true;
        },
        closeInvoiceUploadDialog() {
            this.invoiceUploadDialog = false;
            this.invoiceUploadSubmission = null;
            this.invoiceUploadFile = null;
        },
        confirmInvoiceUpload() {
            if (this.invoiceUploadSubmission && this.invoiceUploadFile) {
                const event = this.invoiceUploadIsPT ? 'pt-upload-invoice' : 'upload-invoice';
                this.$emit(event, this.invoiceUploadSubmission, this.invoiceUploadFile);
                this.closeInvoiceUploadDialog();
            }
        },

        /*
        |----------------------------------------------------------------------
        | Server-side pagination helpers
        |----------------------------------------------------------------------
        */
        _generateQuarterOptions() {
            const options = [{ text: 'All Quarters', value: 'all' }];
            const now = new Date();
            for (let i = 0; i < 12; i++) {
                const d = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
                const q = Math.floor(d.getMonth() / 3) + 1;
                const y = d.getFullYear();
                const key = `Q${q}-${y}`;
                if (!options.find(o => o.value === key)) {
                    options.push({ text: key, value: key });
                }
            }
            return options;
        },
        _buildSubmissionsParams() {
            const dateRange = this.dateRange && this.dateRange.length === 2
                ? [...this.dateRange].sort() : [];
            const paidRange = this.paidDateRange && this.paidDateRange.length === 2
                ? [...this.paidDateRange].sort() : [];
            return {
                page: this.submissionsOptions.page,
                per_page: this.submissionsOptions.itemsPerPage,
                sort_by: this.submissionsSortBy || 'submitted_at',
                sort_dir: this.submissionsSortDesc ? 'desc' : 'asc',
                status: this.statusFilter !== 'all' ? this.statusFilter : undefined,
                user_id: this.userFilter || undefined,
                company_id: this.companyFilter || undefined,
                currency: this.currencyFilter !== 'All' ? this.currencyFilter : undefined,
                submitted_from: dateRange[0] || undefined,
                submitted_to: dateRange[1] || undefined,
                paid_from: paidRange[0] || undefined,
                paid_to: paidRange[1] || undefined,
                quarter: this.quarterFilter !== 'all' ? this.quarterFilter : undefined,
                trashed: this.showDeleted ? 1 : undefined,
                global_dollar_rate: this.dollarRate || undefined,
                global_transfer_fee: this.transferFee !== undefined ? this.transferFee : undefined,
            };
        },
        _buildPTSubmissionsParams() {
            const dateRange = this.ptDateRange && this.ptDateRange.length === 2
                ? [...this.ptDateRange].sort() : [];
            const paidRange = this.ptPaidDateRange && this.ptPaidDateRange.length === 2
                ? [...this.ptPaidDateRange].sort() : [];
            return {
                page: this.ptSubmissionsOptions.page,
                per_page: this.ptSubmissionsOptions.itemsPerPage,
                sort_by: this.ptSubmissionsSortBy || 'submitted_at',
                sort_dir: this.ptSubmissionsSortDesc ? 'desc' : 'asc',
                status: this.ptStatusFilter !== 'all' ? this.ptStatusFilter : undefined,
                user_id: this.ptUserFilter || undefined,
                company_id: this.ptCompanyFilter || undefined,
                currency: this.ptCurrencyFilter !== 'All' ? this.ptCurrencyFilter : undefined,
                submitted_from: dateRange[0] || undefined,
                submitted_to: dateRange[1] || undefined,
                paid_from: paidRange[0] || undefined,
                paid_to: paidRange[1] || undefined,
                quarter: this.ptQuarterFilter !== 'all' ? this.ptQuarterFilter : undefined,
                trashed: this.ptShowDeleted ? 1 : undefined,
                client_name: this.ptClientNameFilter && this.ptClientNameFilter.length ? this.ptClientNameFilter : undefined,
                invoice_status: this.ptInvoiceStatusFilter || undefined,
                ar_status: this.ptArStatusFilter || undefined,
                bank_details: this.ptBankDetailsFilter || undefined,
                client_payment_status: this.ptClientPaymentStatusFilter || undefined,
                time_doctor_status: this.ptTimeDoctorStatusFilter || undefined,
                global_dollar_rate: this.dollarRate || undefined,
                global_transfer_fee: this.transferFee !== undefined ? this.transferFee : undefined,
            };
        },
        async fetchSubmissionsPage() {
            try {
                this.isLoadingPaginatedSubmissions = true;
                const params = this._buildSubmissionsParams();
                const response = await this.$axios.get('/api/payroll/manager/submissions/paginated', params);
                if (response.data.response) {
                    this.paginatedSubmissions = response.data.data || [];
                    this.submissionsTotal = response.data.meta?.total || 0;
                    if (response.data.meta?.pending_count !== undefined) {
                        this.pendingCount = response.data.meta.pending_count;
                    }
                    if (response.data.meta?.totals) {
                        this.serverTotals = response.data.meta.totals;
                    }
                }
            } catch (error) {
                console.error('Error fetching paginated submissions:', error);
                this.$toast.error('Failed to load submissions');
            } finally {
                this.isLoadingPaginatedSubmissions = false;
            }
        },
        async fetchPTSubmissionsPage() {
            try {
                this.isLoadingPaginatedPTSubmissions = true;
                const params = this._buildPTSubmissionsParams();
                const response = await this.$axios.get('/api/payroll/manager/pt/submissions/paginated', params);
                if (response.data.response) {
                    this.paginatedPTSubmissions = response.data.data || [];
                    this.ptSubmissionsTotal = response.data.meta?.total || 0;
                    if (response.data.meta?.pending_count !== undefined) {
                        this.ptPendingCount = response.data.meta.pending_count;
                    }
                    if (response.data.meta?.totals) {
                        this.serverPTTotals = response.data.meta.totals;
                    }
                    if (response.data.meta?.client_names) {
                        this.ptClientNameOptions = response.data.meta.client_names;
                    }
                }
            } catch (error) {
                console.error('Error fetching paginated PT submissions:', error);
                this.$toast.error('Failed to load PT submissions');
            } finally {
                this.isLoadingPaginatedPTSubmissions = false;
            }
        },
        applySubmissionsFilters() {
            this.submissionsOptions = { ...this.submissionsOptions, page: 1 };
            this.fetchSubmissionsPage();
        },
        applyPTSubmissionsFilters() {
            this.ptSubmissionsOptions = { ...this.ptSubmissionsOptions, page: 1 };
            this.fetchPTSubmissionsPage();
        },
    },
    created() {
        this.saveRateTimeout = null;
        if (this.user && this.user.id === 122) {
            this.loadVAProfiles();
        }
    },
    beforeDestroy() {
        if (this.saveRateTimeout) {
            clearTimeout(this.saveRateTimeout);
        }
    },
};
</script>

<style scoped>
.submissions-wrapper {
    overflow-y: auto;
    max-height: 600px;
}

.submission-card {
    transition: all 0.2s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.submission-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.submission-card .v-card__text {
    overflow-y: auto;
    max-height: 300px;
    flex: 1;
}

.detail-label {
    font-size: 0.7rem;
    color: #666;
    font-weight: 600;
    text-transform: uppercase;
}

.detail-value {
    font-size: 0.85rem;
    font-weight: 500;
}

.editable-field {
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 4px;
    border-bottom: 1px dashed #999;
}

.editable-field:hover {
    background-color: rgba(25, 118, 210, 0.1);
    border-bottom-color: #1976d2;
}

.scrollable-table >>> .v-data-table__wrapper {
    max-height: 500px;
    overflow: auto;
}

/* Sticky frozen columns: Actions (80px), Timestamp (120px), Employee (180px) */
.scrollable-table >>> table > thead > tr > th:nth-child(1),
.scrollable-table >>> table > tbody > tr > td:nth-child(1) {
    position: sticky;
    left: 0;
    z-index: 3;
    background: #fff;
}

.scrollable-table >>> table > thead > tr > th:nth-child(2),
.scrollable-table >>> table > tbody > tr > td:nth-child(2) {
    position: sticky;
    left: 80px;
    z-index: 3;
    background: #fff;
}

.scrollable-table >>> table > thead > tr > th:nth-child(3),
.scrollable-table >>> table > tbody > tr > td:nth-child(3) {
    position: sticky;
    left: 200px;
    z-index: 3;
    background: #fff;
}

/* Header cells need higher z-index to stay above sticky body cells */
.scrollable-table >>> table > thead > tr > th:nth-child(1),
.scrollable-table >>> table > thead > tr > th:nth-child(2),
.scrollable-table >>> table > thead > tr > th:nth-child(3) {
    z-index: 5;
}

/* Sticky header row */
.scrollable-table >>> table > thead > tr > th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: #fff;
}

/* Right border on last frozen column for visual separation */
.scrollable-table >>> table > thead > tr > th:nth-child(3),
.scrollable-table >>> table > tbody > tr > td:nth-child(3) {
    border-right: 2px solid #e0e0e0;
}

.scrollable-table >>> .v-select .v-input__slot::before,
.scrollable-table >>> .v-select .v-input__slot::after {
    display: none;
}
</style>